import React, { useEffect, useState, memo, useRef, forwardRef } from 'react'
import ReactDOM from 'react-dom'
import DayPickerInput from 'react-day-picker/DayPickerInput'
import moment from 'moment-timezone'
import { compose } from 'redux'
import { BulletList } from 'react-content-loader'
import { hot } from 'react-hot-loader/root'
import debounce from 'lodash/debounce'
import { withTranslation, useTranslation } from 'react-i18next'
import { Confirm, ThemeProvider } from '@workboard/wobo-ui'
import { Icon } from '@workboard/action-item'
import styled from 'styled-components'
import {
  WbEventHandlerMixins,
  replaceUScoreWithSpace,
  WbMarkdownHtml,
  htmlEntities,
} from '../utils/common'
import { useOnClickOutside } from '../hooks/useOnClickOutside'
import { formatDate, parseDate } from '../utils/date'
import { getAiTypeAttributes, getStateComponentIcon, getActivityTypeName } from '../board/utils'

const StateLineWrapper = styled.div`
  border: 1px solid #dce0e3;
  height: 28px;
  border-radius: 6px;
`

const StateIconWrapper = styled.div`
  width: 40px;
  text-align: center;
  vertical-align: bottom;
  background: ${(props) => (props.isActive ? '#e2f3f8' : 'none')};
  border-right: ${(props) => (props.isLast ? 'none' : '1px solid #DCE0E3')};
  border-top-right-radius: ${(props) => (props.isLast ? '6px' : 'none')};
  border-bottom-right-radius: ${(props) => (props.isLast ? '6px' : 'none')};
  border-top-left-radius: ${(props) => (props.isFirst ? '6px' : 'none')};
  border-bottom-left-radius: ${(props) => (props.isFirst ? '6px' : 'none')};
`

const FilterStateIconWrapper = styled.div`
  svg {
    vertical-align: middle;
  }
`

const AiAttrIconWrapper = styled.span`
  padding-left: 5px;
`

const AiChipContainer = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 9px;
  color: #999999;
  svg {
    vertical-align: middle;
  }

  .ai-chip-left-container {
    display: flex;
    gap: 5px;
  }
`

const AiDueDateText = styled.div`
  color: ${(props) => (props.isLateDate ? '#DB3A1B' : '#999999')};
`

const AiCardTypeIconWrapper = styled.span`
  padding-right: 5px;
  svg {
    vertical-align: middle;
  }
`

const InlineStateIconBox = ({
  name,
  className,
  values,
  value,
  isEnabled,
  onClickEvent,
}) => {
  const valuesLength = values.length ? values.length - 1 : 0

  const onClickInlineState = (e, stateObj) => {
    if (name == 'state') {
      return onClickEvent(e, stateObj.value, isEnabled, value)
    }
    return onClickEvent(e, stateObj.value, isEnabled)
  }

  return (
    <StateLineWrapper className={`displayTable clickable ${className ?? ''}`}>
      <div className="displayRow miniEdits">
        {values.map((state, index) => (
          <StateIconWrapper
            key={`${name}-${state.value}`}
            className="displayCell"
            data-status={value}
            data-attribute={state.value}
            data-enable={isEnabled}
            isActive={state.value == value}
            isLast={index == valuesLength}
            isFirst={index == 0}
            onClick={(e) => onClickInlineState(e, state)}
          >
            <Icon name={state.icons.component} width="18" height="18" />
          </StateIconWrapper>
        ))}
      </div>
    </StateLineWrapper>
  )
}

const FilterStateIcon = ({
  componentName,
  isChecked = false,
  onClickEvent,
  value,
}) => {
  const iconClassName = isChecked
    ? 'icon icon-outline-check_box-24px'
    : 'icon icon-outline-check_box_outline_blank-24px'

  return (
    <FilterStateIconWrapper className="option-wrapper">
      <div
        className={`icon ${iconClassName}`}
        onClick={() => onClickEvent(value)}
      />
      <Icon name={componentName} width="18" height="height" />
    </FilterStateIconWrapper>
  )
}

const InlineEffortBoxWrapper = styled.div`
  border: 1px solid #dce0e3;
  height: 28px;
  border-radius: 6px;
  width: 60px;
  .miniEdits {
    text-align: center;
  }
  .mini-popup-effort-dropdown {
    svg {
      margin-top: 5px;
    }
  }
`

const InlineEffortIconWrapper = styled.div`
  display: flex;
  gap: 8px;
  height: 40px;
  line-height: 40px;
  background-color: ${(props) => (props.isActive ? '#e2f3f8' : 'unset')};
  svg {
    margin: 12px 5px 0px;
  }
  &:hover {
    background-color: #e2f3f8;
  }
`

const InlineEffortBox = ({
  actId,
  label,
  values,
  value,
  isEnabled,
  onClickEvent,
  onShowPopup,
}) => (
  <InlineEffortBoxWrapper className="displayTable effortAiEdit clickable">
    <div className="displayRow miniEdits">
      <div
        className="mini-popup-effort-dropdown"
        onClick={(e) => onShowPopup(e, isEnabled, value)}
        data-enable={isEnabled}
        act-id={actId}
        data-value={value}
      >
        <Icon
          name={getStateComponentIcon(values, value)}
          width="18"
          height="18"
        />
      </div>
      <div
        className="miniEditEffortLists wbActPopup clickable"
        style={{ display: 'none' }}
      >
        <div className="wbEffortArrow whhiteUpArrow" />
        <div className="effortListDivOuterWrap">
          <div className="attrLabel">{label}</div>
          {values.map((effort) => (
            <InlineEffortIconWrapper
              key={`effort-${effort.value}`}
              onClick={(e) => onClickEvent(e, effort.value)}
              className="attrValFont clickable"
              data-value={effort.value}
              data-status={effort.value}
              isActive={effort.value == value}
            >
              <Icon
                name={effort.icons.component ?? ''}
                width="16"
                height="16"
              />
              <div>{effort.label}</div>
            </InlineEffortIconWrapper>
          ))}
        </div>
      </div>
    </div>
  </InlineEffortBoxWrapper>
)

const TopSectionContainer = (props) => {
  const { disableFeature } = props
  const [isLoading, setIsLoading] = useState(true)
  const [showTopTray, setShowTopTray] = useState(
    localStorage.getItem('showTopTray') == 'true'
  )
  const [okrs, setOkrs] = useState([])
  const [workstreams, setWorkstreams] = useState([])
  const [isManager, setIsManager] = useState(false)
  const [isEliteUser, setIsEliteUser] = useState(false)
  const { t } = useTranslation()
  useEffect(() => {
    let isCancelled = false
    $.get(`${wbConfig.baseUrl}/people/thisWeekWorkSmartTray`)
      .then((data) => {
        if (data.people) {
          const {
            personalObjectives = [],
            smartWSs = [],
            is_elite_user = false,
            is_manager = false,
            badge_pull_down = false,
          } = data.people
          if (!isCancelled) {
            setIsLoading(false)
            setOkrs(personalObjectives)
            setWorkstreams(smartWSs)
            setIsEliteUser(is_elite_user)
            setIsManager(is_manager)
          }
        }
        localStorage.setItem('showTopTray', badge_pull_down)
      })
      .catch((error) => {
        console.log(error)
      })
    return () => {
      isCancelled = true
    }
  }, [])

  const toggleThisWeekTop = (e) => {
    e.preventDefault()
    $.post(`${wbConfig.baseUrl}/activity/myworkBadgePullDown`, {
      badgePullDown: showTopTray ? 0 : 1,
    })
    setShowTopTray(!showTopTray)
    localStorage.setItem('showTopTray', !showTopTray)
  }

  return (
    <div id="this-week-top" className={showTopTray ? 'wb-row open' : 'wb-row'}>
      {/* list at most 4 my okrs */}
      <div
        id="this-week-my-okrs"
        className="col-md-8"
        role="table"
        tabIndex="0"
        aria-label={`${t('My OKRs')}`}
      >
        <div className="top-row clearfix">
          <h2 className="top-row-title col-md-10 padding-offset">
            {t("MY OKRS")}
          </h2>
          <a
            href={`${wbConfig.baseUrl}/goal/index`}
            className="btn-link col-md-2 padding-offset"
          >
            {t("See all")}
          </a>
        </div>
        {showTopTray && isLoading && <BulletList width={800} height={130} />}
        {showTopTray && !isLoading && (
          <ThisWeekMyOkr
            myOkrs={okrs}
            isManager={isManager}
            disableFeature={disableFeature}
            t={t}
          />
        )}
      </div>
      {/* list at most 4 my workstreams */}
      <div
        id="this-week-my-workstreams"
        className="col-md-4"
        role="table"
        tabIndex="0"
        aria-label="Workstreams"
      >
        <div className="top-row clearfix">
          <h2 className="top-row-title col-md-10 padding-offset">
            {t("WORKSTREAMS")}
          </h2>
          <a
            href={`${wbConfig.baseUrl}/board/fullBoard`}
            className="btn-link col-md-2 padding-offset"
          >
            {t("See all")}
          </a>
        </div>
        {showTopTray && isLoading && (
          <div className="init-state-wrapper">
            <BulletList width={400} height={130} />
          </div>
        )}
        {showTopTray && !isLoading && (
          <ThisWeekMyWs myWorkstreams={workstreams} t={t}/>
        )}
      </div>
      <div className="separator" />
      <span className="expand-hint">{t("Expand OKRs Workstreams")}</span>
      <button className="btn-toggle-this-weektop" onClick={toggleThisWeekTop}>
        <span className="icon icon-outline-keyboard_arrow_up-24px" />
      </button>
    </div>
  )
}

/*
 * Create a component for this week.
 */
class MyworkApp extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      myWorkData: this.props.myWorkData,
      dataActionsItem: this.props.myWorkData.dateArr,
      selectvalue: this.props.myWorkData.thisWeekAiPref == 0 ? 0 : 1,
      openCalendarFilter: false,
      aiStatus: new Set(['pause', 'doing', 'next']),
      aiRating: new Set(['red', 'amber', 'green']),
      aiPriority: new Set(['low', 'medium', 'high']),
      aiWorkstreamIds: new Set(),
      aiTagIds: new Set(),
      showMeeting: true,
      showSubactions: true,
      meetings: this.props.myWorkData.thisweekMeetingPref == 1,
    }
    this.emptyDOM = this.emptyDOM.bind(this)
    this.updateColumnHeight = this.updateColumnHeight.bind(this)
    this.getCompEnabled = this.getCompEnabled.bind(this)
    this.getValuePosition = this.getValuePosition.bind(this)
    this.registerSortable = this.registerSortable.bind(this)
    this.filterAction = this.filterAction.bind(this)
    this.editAiSmallWindow = this.editAiSmallWindow.bind(this)
    this.openCalendarFilter = this.openCalendarFilter.bind(this)
    this.cardsFilter = this.cardsFilter.bind(this)
    this.closeCalendarFilter = this.closeCalendarFilter.bind(this)
    this.initData = this.initData.bind(this)
    this.callAfterRender = this.callAfterRender.bind(this)
  }

  componentDidMount() {
    // bind a global func for other component to trigger update
    $(document).on('init_this_week_top', {}, this.initData)
    this.callAfterRender()
  }

  callAfterRender() {
    const { actions } = this.props
    const { myWorkData, dataActionsItem } = this.state
    // init the data for top tray from API endpoint
    this.initData()
    if (Object.keys(dataActionsItem).length) {
      let _count = 1
      for (const i in dataActionsItem) {
        this.registerSortable(`connectedSortable-${_count}`)
        _count++
      }
    }
    $('#thisWeekAIs.wsThisWeekAIs .doneDropZone').droppable({
      accept: '.aiContainer:not(.unsortable)',
      tolerance: 'touch',
      out(event, ui) {
        $(this).animate({ height: '45px' }, 100)
      },
      over(event, ui) {
        $(this).animate({ height: '105px' }, 200)
      },
      drop(event, ui) {
        const actID = ui.draggable.attr('data-activity-id')
        const targetId = ui.draggable
          .closest('.connectedSortableParent')
          .attr('id')
        const calViewDataRange1 = $(targetId)
          .find('.connectedSortable')
          .attr('id')
        const calViewDataRange = $(calViewDataRange1)
          .find('.connectedSortable')
          .attr('data-calView')
        if (
          typeof calViewDataRange !== 'undefined' &&
          calViewDataRange != undefined
        ) {
          $(`#${targetId}`).sortable('cancel')
        }
        // $(".connectedSortable").sortable('cancel');
        const postUrl = updateAIState

        $('.doneDropZone').hide('slide', { direction: 'down' }, 200)
        $('.doneDropZone .victory').hide()
        ui.draggable.find('.wbStatus').attr('data-status', 0)
        const state = 0
        const fromWs = 1
        actions.MyworkStatusActionItem(actID, state, fromWs)
        $.post(postUrl, { act_id: actID, state: 0 }, (data, status) => {})
      },
    })
    if (getMaxHeight('.scrollDiv') > 800) {
      $('.scrollDiv').height('auto')
      $('.divider').height(
        getMaxHeight('.scrollDiv') + getMaxHeight('#sortable7') + 300
      )
    }
    const { calView } = myWorkData
    if (
      getMaxHeight('#thisWeekAIs') > 800 &&
      (calView == 'month' || calView == 'week')
    ) {
      $('.cell').height('auto')
      $('.divider').height(getMaxHeight('.cell') + 300)
    }
    this.updateColumnHeight()
    $(document).on('unmount_this_week_app_comp', {}, this.emptyDOM)
    document.body.addEventListener('click', this.closeCalendarFilter)
    // highlight this week on the left menu
    $('.wb-primary-mywork-menu-li.this-week-sec').addClass('active')
  }

  componentWillMount() {
    document.body.removeEventListener('click', this.closeCalendarFilter)
  }

  emptyDOM() {
    if (document.getElementById('thisWeekAIs')) {
      ReactDOM.unmountComponentAtNode(document.getElementById('thisWeekAIs'))
    }
  }

  updateColumnHeight() {
    // use the calculated max height as droppable div height
    const calculatedHeight = $('.fiveDays .divider').first().height()
    $('.fiveDays .scrollDiv .weekdays').each(function () {
      $(this).css('height', calculatedHeight)
    })
  }

  getCompEnabled(keyVal) {
    if (this.state.myWorkData.calView == 'month') {
      return keyVal >= 0
    }
    return keyVal <= 4
  }

  getValuePosition(items = [], value) {
    let positionIndex = 0
    if (items.length) {
      items.map((v, i) => {
        if (v == value) {
          positionIndex = i
        }
      })
    }
    return positionIndex
  }

  registerSortable(selector) {
    const selectorId = `#${selector}`
    const { actions } = this.props
    const _this = this
    $(selectorId).sortable({
      revert: false,
      scroll: true,
      scrollSensitivity: 10,
      scrollSpeed: 30,
      tolerance: 'pointer',
      items: 'div.aiContainer:not(.unsortable)',
      cancel:
        ':input,button,[contenteditable],meeting-calendar-cell,.editAIWrap',
      connectWith: '.connectedSortableParent',
      // containment:"#thisWeekAIs",
      start(e, ui) {
        $('.connectedSortable:not(.weekends)').css({
          height: `${getMaxHeight('.cell') - 72}px`,
        })
        if (ui.item.hasClass('statusEnable')) {
          $('.doneDropZone').show() // not working dropzone for bounceInUp bug id 8290 .addClass('animated bounceInUp');
          $('.doneDropZone .victory').hide()
        }
        $('.editAIWrap').hide()
        // $(".addAI").css("visibility", "hidden");
        $('.hoverDiv').addClass('stopDrag')
        ui.item.find('.aiBlock').addClass('draggedElementPreventClick')
        $('.weekdays').css('min-height', '600px')
        // $(".weekdays").css("height", "auto");
        $('.weekdays').css('padding-bottom', '100px')
        $('#headingCell7').css('margin-top', '0px')
        $('.weekends').css('padding-bottom', '0px')
        ui.placeholder.height(ui.item.height())
        ui.item.attr('data-activity-id')
      },
      stop(event, ui) {
        $('.connectedSortable').css({ height: 'auto' })
        $('.doneDropZone').hide('slide', { direction: 'down' }, 200)
        $('.weekdays').css('min-height', '0px')
        $('.weekdays').css('padding-bottom', '0px')
        $('.weekends').css('padding-bottom', '0px')
        $('#thisWeekAIs:not(.wsThisWeekAIs) #headingCell7').css(
          'margin-top',
          '0px'
        )
        if (getMaxHeight('.cell') > 800) {
          $('.cell').height('auto')
          $('.divider').height(getMaxHeight('.cell') + 70)
          // $("#thisWeekAIs").height(getMaxHeight('.cell') + 200);
        }
        if (getMaxHeight('#thisWeekAIs') > 800) {
          $('.cell').height('auto')
          $('.divider').height(getMaxHeight('.cell') + 300)
        }
        if (getMaxHeight('.scrollDiv') > 800) {
          $('.scrollDiv').height('auto')
          $('.divider').height(
            getMaxHeight('.scrollDiv') + getMaxHeight('#sortable7') + 300
          )
        }
        $('.hoverDiv').removeClass('stopDrag')
        _this.updateColumnHeight()
      },
      receive(event, ui) {
        const enableDrag = ui.item
          .closest('.connectedSortable')
          .attr('data-enable-drag')
        const actID = ui.item.attr('data-activity-id')
        if (enableDrag < 1 || actID.indexOf('newAi_') > -1) {
          $(this).sortable('cancel')
          $(ui.sender).sortable('cancel')
        }
      },
      update(event, ui) {
        const activityIDs = $(selectorId).sortable('toArray', {
          attribute: 'data-activity-id',
        })
        const column_id = $(selectorId).attr('data-due-date')
        const typeView = $(selectorId).attr('data-view')
        const teamView = $(selectorId).attr('data-teamView')
        const tagIdCheck = $(selectorId).attr('data-tag')
        let objectType = 'thisweek'
        let viewType = 'week'
        let objectId = 0
        if (teamView == '1') {
          objectType = 'team'
          objectId = $(selectorId).attr('data-teamid')
        } else if (tagIdCheck != '') {
          objectType = 'workstream'
          viewType = typeView
          objectId = $(selectorId).attr('data-orgTagId')
        }

        let timeOutVal = 0
        if (ui.sender) {
          timeOutVal = 500
        }
        setTimeout(() => {
          $.post(`${wbConfig.baseUrl}/activity/saveSortingPreferences`, {
            items: activityIDs,
            objectType,
            col: column_id,
            objectId,
            viewType,
          }).done(() => {
            myworkCount()
          })
        }, timeOutVal)

        const view = $(event.target)
          .find('.connectedSortable')
          .attr('data-view')
        const enableDrag = 1
        const actID = ui.item.attr('data-activity-id')
        if (view != 'month') {
          const prevDate = ui.item.attr('data-value')
          let dueDate = ui.item
            .closest('.connectedSortable')
            .attr('data-due-date')
          const postUrl = updateAIDueDate
          let startTimeStamp = moment(dueDate, 'YYYY-MM-DD 00:00:00').format(
            'MMM DD,YYYY'
          )
          let dueDateVal = new Date(startTimeStamp).getTime() / 1000
          let positionIndex = 0
          let ids = []
          if (enableDrag > 0 && actID.indexOf('newAi_') == -1) {
            setTimeout(() => {
              if (ui.sender) {
                // let senderId = $(ui.sender).attr('id');
                dueDate = $(selectorId)
                  .find('.connectedSortable')
                  .attr('data-due-date')
                startTimeStamp = moment(dueDate, 'YYYY-MM-DD').format(
                  'MMM DD,YYYY'
                )
                dueDateVal = new Date(dueDate).getTime() / 1000
                ids = $(selectorId).sortable('toArray', {
                  attribute: 'data-activity-id',
                })
                positionIndex = _this.getValuePosition(ids, actID)
                setTimeout(() => {
                  actions.MyworkDragDropDateAi({
                    actID,
                    due_date: dueDateVal,
                    prevDate,
                    positionIndex,
                  })
                  if (prevDate != dueDateVal) {
                    $(ui.item).remove()
                  }
                }, 0)
                $.post(
                  postUrl,
                  { due_date: dueDate, act_id: actID, view: 'week' },
                  (data, status) => {}
                )
              } else {
              }
            }, 0)
          }
        } else if (enableDrag > 0 && actID.indexOf('newAi_') == -1) {
          const calViewDataRange = $(selectorId)
            .find('.connectedSortable')
            .attr('data-calView')
          const dateCal = calViewDataRange.split('@')
          ws_calender_datepicker_popup.find('.confimContentBlock').html('')
          $.each(dateCal, (index, value) => {
            const va = value.split('~')
            ws_calender_datepicker_popup
              .find('.confimContentBlock')
              .append(
                `<p class="confirmText modalParaText" data-due-date="${va[0]}">${va[1]}</p>`
              )
          })
          ws_calender_datepicker_popup
            .data('sortable', this)
            .data('act_id', actID)
            .data('cancelPopupKey', $(ui.sender))
            .dialogOpen()
        }
      },
      over(e, ui) {
        if (ui.sender) {
          const senderId = $(ui.sender).attr('id')
          const targetId = $(e.target).attr('id')
          if (senderId != targetId) {
            const pos = ui.position
            $(ui.sender).sortable('option', 'scroll', false)
            let pp = pos.top - $(e.target).position().top
            pp = pp < 0 ? 0 : pp
            if (pp > 80) {
              pp = (ui.offset.top * 1.5) / 100 + $(e.target).scrollTop()
            }
            $(e.target).scrollTop(Math.abs(pp))
          } else {
            $(ui.sender).sortable('option', 'scroll', true)
          }
        }
      },
    })
  }

  filterAction(filterId) {
    const actionItemLists = this.state.dataActionsItem
    if (filterId == 0) {
      Object.keys(actionItemLists).map((rows) => {
        actionItemLists[rows].map((items) => {
          if (Object.keys(items).length > 0) {
            return (actionItemLists[rows] = actionItemLists[rows].filter(
              (t) => t.parent == null
            ))
          }
        })
        return actionItemLists[rows]
      })
      this.setState({
        dataActionsItem: actionItemLists,
        selectvalue: 0,
      })
    } else {
      this.setState({
        dataActionsItem: this.state.myWorkData.dateArr,
        selectvalue: 1,
      })
    }
  }

  editAiSmallWindow(e, id) {
    const keepOpen = this.state.menuKeepOpen
    const _this = e.target
    if ($(_this).hasClass('unsortable')) {
      return
    }
    if ($(_this).hasClass('draggedElementPreventClick')) {
      $(_this).removeClass('draggedElementPreventClick')
      return
    }
    let obj = $(`#aiBlock_${id}`)
    if (obj.hasClass('aiAttrBlock')) {
      obj = $(`#aiBlock_${id}`)
    }
    if (obj.hasClass('wsTitle')) {
      obj = $(`#aiBlock_${id}`)
    }
    if (obj.hasClass('aiDesc')) {
      obj = $(`#aiBlock_${id}`)
    }
    // hide previously opened mini edit windows
    $('.editAIWrap').hide()
    $('.miniEditVrtArrow').hide()
    $('.wsBoardOwnerListWrapperDiv').hide()
    $('.aiBlock').removeClass('selectedCell')
    if (
      typeof $(obj).attr('data-url') !== 'undefined' &&
      $(obj).attr('data-url') != '' &&
      (e.ctrlKey || e.metaKey)
    ) {
      clearSelection()
      const newHref = $(obj).attr('data-url')
      const viewAIV1Url = newHref.replace('ActionItemDetails', 'aI')
      try {
        const aiPopupUrl = viewAIV1Url.replace('aI?', 'mywork?do=popup&')
        window.open(aiPopupUrl, '_blank')
        window.trigger('focus')
      } catch (err) {
        openFullPopUp(aiFullViewPopUp, viewAIV1Url, this)
      }
      return
    }
    setTimeout(() => {
      // perform single-click action
      $(obj).siblings('.editAIWrap').hide()
      clearSelection()
      // hide previously opened mini edit windows
      $('.miniEditVrtArrow').hide()
      $('.editAIWrap').hide()
      $('.aiBlock').removeClass('selectedCell')
      // $(obj).addClass("selectedCell");
      const offset = $(obj).position()
      const winWith = $(window).width()
      let { left } = offset
      const mgLeft = 0
      let diffVal = 400
      let { top } = offset

      if (keepOpen === 'true') {
        diffVal = 550
      }
      if (winWith - left < diffVal) {
        left -= 350
      }
      if (left > 760 && left < 999) {
        left -= 260
      }
      if (left > 1000) {
        left -= 240
      }
      if (navigator.appVersion.indexOf('Mac') != -1) {
        left -= 40
      }
      if (top < 150) {
        if ($('#mywork_tabs_container').length) {
          top -= 190
        } else {
          top -= 230
        }
      } else if ($('#mywork_tabs_container').length) {
        if ($('#this-week-top').hasClass('open')) {
          top -= 280
        }
        /* else {
				  top = 0;
				} */
      } else if (top > 310) {
        top -= 330
      } else {
        top -= 295
      }

      $(obj)
        .siblings('.editAIWrap')
        .show()
        .css({
          'margin-top': 0,
          'margin-left': mgLeft - 50,
          top,
          left: left + 120,
          'z-index': 20,
          position: 'absolute',
        })
        .addClass('dialogbox-shadow')
    }, 250)
  }

  openCalendarFilter(e) {
    e.preventDefault()
    this.setState({
      openCalendarFilter: !this.state.openCalendarFilter,
    })
  }

  /* same function as previous implemented in ThisweekComp */
  navWeek(e) {
    showLoaderWidget($('#content'))
    const _this = this
    const myworkData = this.state.myWorkData
    const { tagId, calView, teamId } = myworkData
    const dateStart = $(e.target).attr('data-start')
    let params = {
      type: 'thisweek',
      offset: 0,
      limit: 50,
      dateStart,
      json: true,
    }
    if (tagId) {
      params = {
        ...params,
        tagId,
        teamId,
        _view: calView,
        currentPage: 'mywork',
      }
    } else {
      params = {
        ...params,
        currentPage: 'mywork',
        activeTab: 2,
        selTab: 2,
      }
    }
    $.get(`${wbConfig.baseUrl}/activity/getMyWorkActivity`, params)
      .then((data) => {
        const meetingPref = data.thisweekMeetingPref == 1
        _this.setState({
          myWorkData: data,
          dataActionsItem: data.dateArr,
          meetings: meetingPref,
          selectvalue: data.thisWeekAiPref == 0 ? 0 : 1,
        })
        // console.log('data -->', data);
        _this.props.actions.reloadMyWorkData(data)
        _this.callAfterRender()
        // _this.cardsFilter("meetings", meetingPref);
        removeLoaderWidget()
      })
      .catch((error) => {
        console.log(error)
        removeLoaderWidget()
      })
  }

  /* front end filter function(ais, meetings, subactions) */
  cardsFilter(item, value) {
    let key = item
    if (item === 'pause' || item === 'doing' || item === 'next') {
      key = 'status'
    } else if (item === 'red' || item === 'amber' || item === 'green') {
      key = 'rating'
    } else if (item === 'low' || item === 'medium' || item === 'high') {
      key = 'priority'
    }
    switch (key) {
      case 'meetings':
        this.setState({
          showMeeting: value,
        })
        break
      case 'subactions':
        this.setState({
          showSubactions: value,
        })
        break
      case 'status':
        const status = this.state.aiStatus
        if (value) {
          // check
          if (!status.has(item)) {
            status.add(item)
          }
        } else {
          // uncheck
          status.delete(item)
        }
        this.setState({
          aiStatus: status,
        })
        break
      case 'rating':
        const rating = this.state.aiRating
        if (value) {
          // check
          if (!rating.has(item)) {
            rating.add(item)
          }
        } else {
          // uncheck
          rating.delete(item)
        }
        this.setState({
          aiRating: rating,
        })
        break
      case 'priority':
        const priority = this.state.aiPriority
        if (value) {
          // check
          if (!priority.has(item)) {
            priority.add(item)
          }
        } else {
          // uncheck
          priority.delete(item)
        }
        this.setState({
          aiPriority: priority,
        })
        break
      case 'workstreams':
        const wsIds = value.reduce((accu, ws) => accu.concat(ws.id), [])
        this.setState({
          aiWorkstreamIds: new Set(wsIds),
        })
        break
      case 'tags':
        const tagIds = value.reduce((accu, tag) => accu.concat(tag.tagId), [])
        this.setState({
          aiTagIds: new Set(tagIds),
        })
        break
    }
  }

  /* close filter calendar if click elsewhere */
  /**
   * note: pure JS function: we are not using jQuery in the future
   * we have to trace all the way up to body tag to make sure click trigger
   * is not within calendar filter itself, if it's inside the calendar filter,
   * we will not close the filter.
   */
  closeCalendarFilter(e) {
    // check if the calendar is open
    if (this.state.openCalendarFilter) {
      let currNode = e.target
      let inCalendarFilter = false
      while (currNode.parentNode !== document.body) {
        if (currNode.parentNode.classList.contains('calendar-filter')) {
          inCalendarFilter = true
        }
        currNode = currNode.parentNode
      }
      if (!inCalendarFilter) {
        this.setState({
          openCalendarFilter: false,
        })
      }
    }
  }

  /* init data from API */
  initData() {
    // get top okrs, ws data
    this.cardsFilter('meetings', this.state.meetings)
  }

  handleMeetings(selected) {
    const selectedVal = !this.state[selected]
    this.cardsFilter(selected, selectedVal)
    this.setState({
      [selected]: selectedVal,
    })
    const params = {
      thisweekMeetingPref: selectedVal,
    }
    if ($.ajaxSetup().data.YII_CSRF_TOKEN) {
      params.YII_CSRF_TOKEN = $.ajaxSetup().data.YII_CSRF_TOKEN
    }
    $.post(`${wbConfig.baseUrl}/activity/thisweekMeetingPref`, params)
  }

  render() {
    const myworkData = this.state.myWorkData
    const { tagId, calView } = myworkData
    const { dataActionsItem } = this.state
    const teamView = myworkData.teamView == ''
    const TagId = tagId == ''
    let constantCheck = TagId ? 5 : 6
    constantCheck = calView == 'month' ? 4 : constantCheck
    const sortArr = myworkData.sorting
    const disableFeature = myworkData.disablePersonalOkr
    const { t } = this.props
    return (
      <section>
        {/* <ThisWeekTop /> */}
        {TagId && <TopSectionContainer disableFeature={disableFeature} />}
        {/* top action bar above the calendar */}
        <div className="action-bar clearfix">
          <div
            className="col-lg-8 col-md-7 padding-offset"
            aria-label={`${moment(myworkData.dateStart).format('MMMM YYYY')}`}
          >
            <span className="btn-nav-week prevweek">
              <span
                className="icon icon-outline-arrow_back_ios-24px"
                data-start={myworkData.prevDate}
                onClick={(e) => this.navWeek(e)}
                aria-label="Previous"
              />
            </span>
            <span className="btn-nav-week nextweek">
              <span
                className="icon icon-outline-arrow_forward_ios-24px"
                data-start={myworkData.nextDate}
                onClick={(e) => this.navWeek(e)}
                aria-label="Next"
              />
            </span>
            <span className="calendar-year-month" tabIndex="0">
              {moment(myworkData.dateStart).format('MMMM YYYY')}
            </span>
          </div>
          <div className="col-lg-4 col-md-5 padding-offset filterwrapper">
            {window.hasLayout && (
              <div className="favorite">
                <div
                  className="pin pinToday"
                  data-pinstatus="0"
                  data-type="activity_mywork"
                  data-id="2"
                  title="Pin to favorites"
                  role="button"
                  tabIndex="0"
                  ariaLabel="Pin to favorites"
                />
                <div
                  className="clickable printTeam"
                  title="Export"
                  role="button"
                  tabIndex="0"
                  ariaLabel="Export"
                >
                  <i className="icon icon-outline-publish-24px" />
                </div>
              </div>
            )}
            <div className="single-row">
              <span
                className={
                  this.state.meetings
                    ? 'icon icon-outline-check_box-24px'
                    : 'icon icon-outline-check_box_outline_blank-24px'
                }
                onClick={() => this.handleMeetings('meetings')}
                role="checkbox"
                tabIndex="0"
                aria-label="Show meetings"
              />
              <span className="option-text">{t('Show meetings')}</span>
            </div>
            <div className="calendar-filter">
              <span>{t('Quick Filter')}</span>
              <button
                className="btn-nav-week open-ai-filter"
                onClick={this.openCalendarFilter}
                tabIndex="0"
                aria-label="Quick Filter"
              >
                <span className="icon icon-outline-filter_list-24px" />
              </button>
              <CalendarFilter
                open={this.state.openCalendarFilter}
                cardsFilter={this.cardsFilter}
                t={t}
              />
            </div>
          </div>
        </div>
        <div
          id="this-week-bottom"
          className="clearfix"
          tabIndex="-1"
          aria-label="Calendar"
        >
          {Object.keys(dataActionsItem).length > 0 &&
            Object.keys(dataActionsItem).map((item, index) => (
              <ThisweekComp
                myworkData={myworkData}
                dateAi={sortAis(
                  dataActionsItem[item],
                  getColSortArr(sortArr, item)
                )}
                keys={index}
                list={item}
                compEnabled={this.getCompEnabled(index)}
                constantCheck={constantCheck}
                editAiSmallWindow={this.editAiSmallWindow}
                actions={this.props.actions}
                key={item}
                showMeeting={this.state.showMeeting}
                showSubactions={this.state.showSubactions}
                aiStatus={this.state.aiStatus}
                aiRating={this.state.aiRating}
                aiPriority={this.state.aiPriority}
                aiWorkstreamIds={this.state.aiWorkstreamIds}
                aiTagIds={this.state.aiTagIds}
                {...this.props}
              />
            ))}
          {calView != 'month' && (
            <ThisWeekNextColum
              myworkData={this.state.myWorkData}
              constantCheck={constantCheck}
              dateAis={dataActionsItem}
              editAiSmallWindow={this.editAiSmallWindow}
              actions={this.props.actions}
              showMeeting={this.state.showMeeting}
              showSubactions={this.state.showSubactions}
              aiStatus={this.state.aiStatus}
              aiRating={this.state.aiRating}
              aiPriority={this.state.aiPriority}
              aiWorkstreamIds={this.state.aiWorkstreamIds}
              aiTagIds={this.state.aiTagIds}
              {...this.props}
            />
          )}
        </div>
      </section>
    )
  }
}

const ThisWeekMyOkr = (props) => {
  const { myOkrs, disableFeature, isManager, t } = props
  const openKrDialog = (e, id) => {
    // calling a global func to trigger open dialog
    viewGoalDetailsPage($(e.target), { id })
  }

  return (
    <div className="list-block-wrapper">
      {myOkrs.length > 0 && (
        <ul>
          {myOkrs.map((myOkr, index) => (
            <li
              className="myokr-row clearfix"
              key={`my_okrs_${myOkr.goal_id}`}
              role="row"
              tabIndex="0"
            >
              <div className="col-md-1 padding-offset">
                <span
                  className="percentage"
                  style={{ color: myOkr.color }}
                  role="cell"
                >
                  {Number(myOkr.progress) >= 0 && `${myOkr.progress}%`}
                  {Number(myOkr.progress) < 0 && '--'}
                </span>
              </div>
              <div className="col-md-10 padding-offset">
                <h5
                  className="okr-title"
                  onClick={(e) => {
                    openKrDialog(e, myOkr.goal_id)
                  }}
                  dangerouslySetInnerHTML={WbMarkdownHtml(
                    myOkr.goal_name.length > 140
                      ? htmlEntities(myOkr.goal_name.substring(0, 140))
                      : htmlEntities(myOkr.goal_name)
                  )}
                  role="cell"
                />
              </div>
              <div className="col-md-1 padding-offset">
                {myOkr.krs_due_for_update > 0 && (
                  <div className="past-due" role="cell">
                    <span className="count">{myOkr.krs_due_for_update}</span>
                    <span className="icon-clock" />
                    <div className="tooltip">
                      {myOkr.krs_due_for_update +
                        (myOkr.krs_due_for_update > 1
                          ? ` ${t('Key Results')}past due`
                          : ` ${t('Key Result')} past due`)}
                    </div>
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
      {myOkrs.length === 0 && (
        <div className="empty-state-okrs">
          <div className="empty-state-container">
            <p>You currently don’t have any {t('OKRs')}.</p>
            {disableFeature && (
              <a
                href="#"
                className="btn-empty-action btn-add-okr"
                data-mygoal="1"
              >
                + Add Personal {t('OKR')}
              </a>
            )}
            {isManager && (
              <a href="#" className="btn-empty-action btn-add-okr">
                + Add Team {t('OKR')}
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

const ThisWeekMyWs = (props) => {
  const { t, myWorkstreams } = props
  return (
    <div
      className={
        myWorkstreams.length === 0
          ? 'list-block-wrapper empty'
          : 'list-block-wrapper'
      }
    >
      {myWorkstreams.length > 0 && (
        <ul>
          {myWorkstreams.map((myws, index) => (
            <li
              className="myokr-row clearfix"
              key={`my_workstreams_${myws.id}`}
              role="row"
              tabIndex="0"
            >
              <div className="col-md-8 padding-offset">
                <h5 className="okr-title">
                  <a
                    href={`${wbConfig.baseUrl}/activity/workstreams?id=${myws.id}`}
                    tabIndex="-1"
                  >
                    <span
                      dangerouslySetInnerHTML={WbMarkdownHtml(
                        myws.name.length > 100
                          ? myws.name.substring(0, 100)
                          : myws.name
                      )}
                    />
                  </a>
                  <div
                    className="tooltip"
                    dangerouslySetInnerHTML={WbMarkdownHtml(myws.name)}
                  />
                </h5>
              </div>
              <div className="col-md-4 padding-offset">
                {(myws.late_ais > 0 || myws.doing_ais > 0) && (
                  <div className="action-items">
                    {myws.late_ais > 0 && (
                      <span
                        className="late-actions"
                        aria-label={`${myws.late_ais} late actions`}
                      >
                        <span className="count">{myws.late_ais}</span>
                        <span className="icon-hourglass" />
                      </span>
                    )}
                    {myws.doing_ais > 0 && (
                      <span
                        className="active-actions"
                        aria-label={`${myws.doing_ais} active actions`}
                      >
                        <span className="count">{myws.doing_ais}</span>
                        <span className="icon-doing" />
                      </span>
                    )}
                    <div className="tooltip">
                      {myws.doing_ais > 0 && `${myws.doing_ais} in progress`}
                      {myws.doing_ais > 0 && myws.late_ais > 0 && ' | '}
                      {myws.late_ais > 0 && `${myws.late_ais} late`}
                    </div>
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
      {myWorkstreams.length === 0 && (
        <div className="empty-state-workstreams">
          <img
            src={`${wbConfig.baseUrl}/images/mywork/workstream_empty.png`}
            className="empty-img-placeholder"
          />
          <p>
            {t('Your pinned workstreams')}
            <br />
            {t('will show here.')}
          </p>
          <a href="#" className="btn-empty-action btn-add-workstream">
            + {t('Add Workstream')}
          </a>
        </div>
      )}
    </div>
  )
}

class CalendarFilter extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      pause: true,
      doing: true,
      next: true,
      red: true,
      amber: true,
      green: true,
      low: true,
      medium: true,
      high: true,
      meetings: true,
      subactions: true,
      showWsResults: false,
      wsResults: [],
      selectedWs: [],
      showTagsResults: false,
      tagsResults: [],
      selectedTags: [],
    }
    this.searchTagsDebounce = debounce(this.searchTags, 1500)
    this.searchWsDebounce = debounce(this.searchWs, 1500)
    this.clearSearchInput = this.clearSearchInput.bind(this)
    this.handleCheckbox = this.handleCheckbox.bind(this)
  }

  componentDidMount() {
    document.body.addEventListener('click', this.clearSearchInput)
  }

  componentWillUnmount() {
    document.body.removeEventListener('click', this.clearSearchInput)
  }

  handleCheckbox(selected) {
    this.props.cardsFilter(selected, !this.state[selected])
    this.setState({
      [selected]: !this.state[selected],
    })
  }

  selectWs(wsId, checked) {
    const _this = this
    if (checked) {
      // disable workstream
      const curr = this.state.selectedWs.filter((value) => value.id !== wsId)
      _this.setState({
        selectedWs: curr,
        showWsResults: true,
      })
      this.props.cardsFilter('workstreams', curr)
    } else {
      // enable workstream
      const toAdd = this.state.wsResults.filter((value) => value.id === wsId)
      const curr = this.state.selectedWs
      curr.push(toAdd[0])
      _this.setState({
        selectedWs: curr,
        showWsResults: false,
      })
      // clear the input text
      document.getElementById('filter-search-ws').value = ''
      this.props.cardsFilter('workstreams', curr)
    }
  }

  searchWsOnChange(e) {
    e.persist()
    this.searchWsDebounce(e)
  }

  searchWs(e) {
    const _this = this
    const searchText = e.target.value.trim()
    if (searchText !== '') {
      $.get(`${wbConfig.baseUrl}/tag/getMyWs?term=${searchText}`)
        .then((response) => {
          response = JSON.parse(response)
          if (response.data.length > 0) {
            const result = []
            for (let i = 0; i < response.data.length; i++) {
              const data = response.data[i]
              if (data.ws.length > 0) {
                for (let j = 0; j < data.ws.length; j++) {
                  const temp = {
                    team_id: data.team_id,
                    team_name: data.team_name,
                    id: data.ws[j].id,
                    name: data.ws[j].name,
                  }
                  result.push(temp)
                }
              }
            }
            _this.setState({
              wsResults: result,
              showWsResults: true,
              showTagsResults: false,
            })
          } else {
            _this.setState({
              wsResults: [],
              showWsResults: false,
              showTagsResults: false,
            })
          }
        })
        .catch((error) => {
          console.log(error)
        })
    } else {
      _this.setState({
        showWsResults: false,
        wsResults: [],
      })
    }
    // clear the input text on tags
    document.getElementById('filter-search-tag').value = ''
  }

  unselectWs(e, wsId) {
    const curr = this.state.selectedWs.filter((value) => value.id !== wsId)
    this.setState({
      selectedWs: curr,
      showTagsResults: false,
    })
    this.props.cardsFilter('workstreams', curr)
  }

  isWsSelected(wsId) {
    for (let i = 0; i < this.state.selectedWs.length; i++) {
      if (this.state.selectedWs[i].id === wsId) {
        return true
      }
    }
    return false
  }

  selectTags(tagId, checked) {
    const _this = this
    if (checked) {
      // disable tag
      const curr = this.state.selectedTags.filter(
        (value) => value.tagId !== tagId
      )
      _this.setState({
        selectedTags: curr,
        showTagsResults: true,
      })
      this.props.cardsFilter('tags', curr)
    } else {
      // enable tag
      const toAdd = this.state.tagsResults.filter(
        (value) => value.tagId === tagId
      )
      const curr = this.state.selectedTags
      curr.push(toAdd[0])
      _this.setState({
        selectedTags: curr,
        showTagsResults: false,
      })
      // clear the input text
      document.getElementById('filter-search-tag').value = ''
      this.props.cardsFilter('tags', curr)
    }
  }

  searchTagsOnChange(e) {
    e.persist()
    this.searchTagsDebounce(e)
  }

  searchTags(e) {
    const _this = this
    const searchText = e.target.value.trim()
    if (searchText !== '') {
      $.get(`${wbConfig.baseUrl}/activity/findTags?term=${searchText}`)
        .then((response) => {
          response = JSON.parse(response)
          if (Object.keys(response.data).length > 0) {
            _this.setState({
              showTagsResults: true,
              tagsResults: Object.values(response.data),
              showWsResults: false,
            })
          } else {
            _this.setState({
              showTagsResults: false,
              tagsResults: [],
              showWsResults: false,
            })
          }
        })
        .catch((error) => {
          console.log(error)
        })
    } else {
      _this.setState({
        showTagsResults: false,
        tagsResults: [],
      })
    }
    // clear the input text on workstreams
    document.getElementById('filter-search-ws').value = ''
  }

  unselectTag(e, tagId) {
    const curr = this.state.selectedTags.filter(
      (value) => value.tagId !== tagId
    )
    this.setState({
      selectedTags: curr,
      showTagsResults: false,
    })
    this.props.cardsFilter('tags', curr)
  }

  isTagSelected(tagId) {
    for (let i = 0; i < this.state.selectedTags.length; i++) {
      if (this.state.selectedTags[i].tagId === tagId) {
        return true
      }
    }
    return false
  }

  clearSearchInput(e) {
    const _this = this
    // check if the results are open
    if (this.state.showWsResults || this.state.showTagsResults) {
      let currNode = e.target
      let inCalendarFilter = false
      while (currNode.parentNode !== document.body) {
        if (currNode.parentNode.classList.contains('calendar-filter')) {
          inCalendarFilter = true
        }
        currNode = currNode.parentNode
      }
      if (!inCalendarFilter) {
        _this.setState({
          showWsResults: false,
          showTagsResults: false,
        })
      }
    }
    // clear the input text on tags
    if (document.getElementById('filter-search-ws')) {
      document.getElementById('filter-search-ws').value = ''
    }
    if (document.getElementById('filter-search-tag')) {
      document.getElementById('filter-search-tag').value = ''
    }
  }

  getAIStateOption(name) {
    const { attributes } = getAiTypeAttributes(0)
    const { values } = attributes?.[name]
    if (name == 'state') {
      const stateMap = {
        1: 'doing',
        2: 'next',
        3: 'pause',
      }
      return values
        .filter((v) => v.value != 0)
        .map((v) => ({ ...v, inputValue: stateMap[v.value] }))
    }
    if (name == 'rag') {
      const ragMap = {
        0: 'green',
        1: 'amber',
        2: 'red',
      }
      return values.map((v) => ({ ...v, inputValue: ragMap[v.value] }))
    }
    if (name == 'priority') {
      const priorityMap = {
        1: 'high',
        2: 'medium',
        3: 'low',
      }
      return values.map((v) => ({ ...v, inputValue: priorityMap[v.value] }))
    }
    return []
  }
 

  render() {
    const { t } = this.props
    return (
      <div id="calendar-filter" className={this.props.open ? 'open' : ''}>
        <h6>{t('Show me:')}</h6>
        <div className="status-row">
          <label>{t('STATUS')}</label>
          {this.getAIStateOption('state').map((state) => (
            <FilterStateIcon
              key={`state-${state.value}`}
              componentName={state?.icons?.component}
              isChecked={this.state?.[state.inputValue]}
              onClickEvent={this.handleCheckbox}
              value={state.inputValue}
            />
          ))}
        </div>
        <div className="status-row">
          <label>{t('RATING')}</label>
          {this.getAIStateOption('rag').map((rag) => (
            <FilterStateIcon
              key={`rag-${rag.value}`}
              componentName={rag?.icons?.component}
              isChecked={this.state?.[rag.inputValue]}
              onClickEvent={this.handleCheckbox}
              value={rag.inputValue}
            />
          ))}
        </div>
        <div className="status-row">
          <label>{t('PRIORITY')}</label>
          {this.getAIStateOption('priority').map((priority) => (
            <FilterStateIcon
              key={`priority-${priority.value}`}
              componentName={priority?.icons?.component}
              isChecked={this.state?.[priority.inputValue]}
              onClickEvent={this.handleCheckbox}
              value={priority.inputValue}
            />
          ))}
        </div>
        <div className="status-row">
          <label>{t('WORKSTREAMS')}</label>
          <div className="search-input-wrapper">
            <i className="icon icon-outline-search-18px input-icon-placeholder" />
            <input
              id="filter-search-ws"
              className="searchInput"
              type="text"
              placeholder={t("Search for teams or workstreams")}
              onChange={(e) => {
                this.searchWsOnChange(e)
              }}
            />
            <div
              className={
                this.state.showWsResults
                  ? 'search-input-result show'
                  : 'search-input-result'
              }
            >
              <ul>
                {this.state.wsResults.map((ws) => {
                  const isWsSelected = this.isWsSelected(ws.id)
                  return (
                    <li
                      className="ws-wrapper"
                      onClick={() => {
                        this.selectWs(ws.id, isWsSelected)
                      }}
                      key={`ws-result-${ws.id}`}
                    >
                      <span
                        className={
                          isWsSelected
                            ? 'icon icon-outline-check_box-24px'
                            : 'icon icon-outline-check_box_outline_blank-24px'
                        }
                      />
                      <div className="ws-name-block">
                        <label className="ws-name">{ws.name}</label>
                        <p className="team-name">{ws.team_name}</p>
                      </div>
                    </li>
                  )
                })}
              </ul>
            </div>
          </div>
          <div className="selected-results">
            <ul>
              {this.state.selectedWs.map((ws) => (
                <li key={`selected-ws-${ws.id}`}>
                  <div className="selected-option">
                    <div className="ws-name-block">
                      <label className="ws-name">{ws.name}</label>
                      <p className="team-name">{ws.team_name}</p>
                    </div>
                    <span
                      className="icon-close"
                      onClick={(e) => {
                        this.unselectWs(e, ws.id)
                      }}
                    />
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="status-row">
          <label>{t("TAGS")}</label>
          <div className="search-input-wrapper">
            <i className="icon icon-outline-search-18px input-icon-placeholder" />
            <input
              id="filter-search-tag"
              className="searchInput"
              type="text"
              placeholder={t("Search for tags")}
              onChange={(e) => {
                this.searchTagsOnChange(e)
              }}
            />
            <div
              className={
                this.state.showTagsResults
                  ? 'search-input-result show'
                  : 'search-input-result'
              }
            >
              <ul>
                {this.state.tagsResults.map((tag) => {
                  const isTagSelected = this.isTagSelected(tag.tagId)
                  return (
                    <li
                      className="tag-wrapper"
                      onClick={() => {
                        this.selectTags(tag.tagId, isTagSelected)
                      }}
                      key={`tags-result-${tag.tagId}`}
                    >
                      <span
                        className={
                          isTagSelected
                            ? 'icon icon-outline-check_box-24px'
                            : 'icon icon-outline-check_box_outline_blank-24px'
                        }
                      />
                      {tag.type === 1 && (
                        <img src="/wb/images/mywork/tag-personal.png" />
                      )}
                      {tag.type === 2 && (
                        <img src="/wb/images/mywork/tag-group.png" />
                      )}
                      <span className="text">{tag.name}</span>
                    </li>
                  )
                })}
              </ul>
            </div>
          </div>
          <div className="selected-results">
            <ul>
              {this.state.selectedTags.map((tag) => (
                <li key={`selected-tags-${tag.tagId}`}>
                  <div className="selected-option">
                    {tag.type === 1 && (
                      <img src="/wb/images/mywork/tag-personal.png" />
                    )}
                    {tag.type === 2 && (
                      <img src="/wb/images/mywork/tag-group.png" />
                    )}
                    <span className="text">{tag.name}</span>
                    <span
                      className="icon-close"
                      onClick={(e) => {
                        this.unselectTag(e, tag.tagId)
                      }}
                    />
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
        {/* <div className="single-row">
          <div className="option-wrapper">
            <span className={this.state.meetings ? 'option-checkbox check' : 'option-checkbox'} onClick={() => this.handleCheckbox('meetings')} />
            <span className="option-text">Show meetings</span>
          </div>
        </div> */}
        <div className="single-row">
          <div className="option-wrapper">
            <span
              className={
                this.state.subactions
                  ? 'icon icon-outline-check_box-24px'
                  : 'icon icon-outline-check_box_outline_blank-24px'
              }
              onClick={() => this.handleCheckbox('subactions')}
            />
            <span className="option-text">{t('Subtasks')}</span>
          </div>
        </div>
      </div>
    )
  }
}

const CascadeKRCard = memo((props) => {
  const { kr } = props
  const { t } = props
  const url = `${wbConfig.baseUrl}/okrs?openCascade=1`
  return (
    <div
      className="cascadekr-calendar-cell"
      data-testid="cascadekr-calendar-cell"
    >
      <a target="_blank" href={url} rel="noreferrer">
        <div className="kr-text">
          {t("Pick up the key result")} {kr.firstname}{' '}
          {kr.lastname.length > 0 ? `${kr.lastname[0].toUpperCase()}.` : ''}{' '}
          {t("cascaded to you")}
        </div>
      </a>
    </div>
  )
})

const getColSortArr = function (arrVal = {}, index) {
  try {
    const ind = replaceUScoreWithSpace(`col_sorting_${index}`)
    const val = arrVal[ind]
    return val !== 'undefined' ? val : []
  } catch (err) {
    // console.log(err)
  }
}

const sortAis = function (items = [], arr = []) {
  if (items.length) {
    // get new temp ais
    let results = items.filter((item) => item.id.indexOf('newAi_') > -1)
    const remainingItems = items.filter(
      (item) => item.id.indexOf('newAi_') === -1
    )
    if (arr && arr !== 'undefined' && arr.length) {
      arr.map((sort) => {
        const find = remainingItems.find((i) => i.id == sort)
        if (find) {
          results.push(find)
        }
      })
      const notInSortArray = remainingItems.filter(
        (item) => arr.indexOf(item.id) === -1
      )
      results = results.concat(notInSortArray)
    } else {
      results = items
    }
    return results
  }
}

/*
 * Create a component for this week for month and week for first 4 or 5 column.
 */
const ThisweekComp = (props) => {
  const { actions, keys, list, compEnabled, editAiSmallWindow, t } = props
  const dataAiList = props.dateAi
  const {
    currentUserId,
    workstreamId,
    workstreamName,
    calViewDataRange,
    imageCdnUrl,
    calView,
    menuKeepOpen,
    teamView,
    teamId,
    editAIURL,
    createUrl,
    loggedInUser,
    orgTagId,
    isTeamManager,
    meetingDays,
    offsetTimzone,
    offset,
  } = props.myworkData

  const [enableComp, setEnableComp] = useState(compEnabled);
  const textareaRef = useRef(null);

  const TagChek = props.myworkData.tagId
  const urlField4 =
    TagChek != '' && TagChek != 'team' ? `&wsId=${orgTagId}` : ''
  const urlField5 = TagChek == 'team' ? `&teamId=${TagChek}&teamView=1` : ''
  const createAI = `${createUrl}?uid=${loggedInUser}&actionType=createAI&aiDueDate=${moment(
    list,
    'YYYY-MM-DD'
  ).format('YYYY-MM-DD')}${urlField4}${urlField5}`
  const dummyUrl = `&uid=${loggedInUser}&actionType=createAI&aiDueDate=${moment(
    list,
    'YYYY-MM-DD'
  ).format('YYYY-MM-DD')}${urlField4}${urlField5}`
  const UserTimeZone =
    props.myworkData.userTimeZone != 'undefined' &&
    props.myworkData.userTimeZone != null
      ? props.myworkData.userTimeZone.timezone
      : null
  const presentDate = new Date()
  const styleObj = { margin: '0px' }
  if (keys == 0 && TagChek == '') {
    styleObj.textAlign = 'center'
  } else {
    styleObj.textAlign = 'center'
  }
  let HighlightPresent = ''
  if (
    UserTimeZone &&
    UserTimeZone != null &&
    moment(presentDate).tz(UserTimeZone).format('YYYY-MM-DD') == list
  ) {
    HighlightPresent = 'headingCellWsCurrent'
    styleObj.backgroundColor = '#52BAD5'
    styleObj.color = '#fff'
    styleObj.fontWeight = '#bold'
  } else {
    styleObj.backgroundColor = 'rgba(204, 204, 204, 0.15)'
  }
  if (calView == 'month') {
    const startOfWeek = moment().startOf('week').toDate()
    const endOfWeek = moment().endOf('week').toDate()
    const formattedFirstDate = moment(startOfWeek).format('YYYY-MM-DD')
    const formattedLastDate = moment(endOfWeek).format('YYYY-MM-DD')
    const currentWeek = `${moment(formattedFirstDate, 'YYYY-MM-DD').format(
      'MMM DD'
    )} - ${moment(formattedLastDate, 'YYYY-MM-DD').format('MMM DD')}`
    const styleObj = { margin: '0px' }
    if (currentWeek == list) {
      HighlightPresent = 'headingCellWsCurrent'
      styleObj.backgroundColor = '#55BAD5'
      styleObj.color = '#fff'
      styleObj.fontWeight = '#bold'
    } else {
      styleObj.backgroundColor = '#f2F2F5'
    }
  }
  const calViewDataRangeVal = calViewDataRange[list]
    ? calViewDataRange[list].text
    : ''
  const col = keys + 1
  let formatDate = list
  let formatStartDate = list
  if (calView == 'month') {
    const str_array = list.split('-')
    const monthStartDate = str_array[0].trim()
    const monthendDate = str_array[1].trim()
    formatStartDate = moment(monthStartDate, 'MMM DD').format('YYYY-MM-DD')
    formatDate = moment(monthendDate, 'MMM DD').format('YYYY-MM-DD')
  }
  const meetEnable = !!(TagChek == '' && calView != 'month')
  const cascadeList = props.myworkData.cascadeList || []
  useEffect(() => {
    // Anything in here is fired on component mount.
    document.body.addEventListener('click', bodyClickHandler, true)
    return () => {
      // Anything in here is fired on component unmount.
      document.body.removeEventListener('click', bodyClickHandler, true)
    }
  }, [])

  const addAIButton = (e, list, calView) => {
    $('.wsBoardAddAiWrappers').hide()
    $('.scrollDiv').removeClass('addAiBoxOpen')
    if (calView == 'month') {
      const str_array = list.split('-')
      const monthendDate = str_array[1].trim()
      const formatDate = moment(monthendDate, 'MMM DD').format('YYYY-MM-DD')
      $(`.wsBoardAddAiWrappers.this-week-aiform-${formatDate}`).show()
      $(`.wsBoardAddAiWrappers.this-week-aiform-${formatDate}`)
        .next('.scrollDiv')
        .addClass('addAiBoxOpen')
      $(`.this-week-aiform-${formatDate} .thisWeekAddAi `).trigger('focus')
    } else {
      $(`.wsBoardAddAiWrappers.this-week-aiform-${list}`).show()
      $(`.wsBoardAddAiWrappers.this-week-aiform-${list}`)
        .next('.scrollDiv')
        .addClass('addAiBoxOpen')
      $(`.this-week-aiform-${list} .thisWeekAddAi `).trigger('focus')
    }
    // Focus the textarea
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }

  const bodyClickHandler = (e) => {
    if (!$('.addAIArea').is(e.target) && !$('.thisWeekAddAi').is(e.target)) {
      $('.wsBoardAddAiWrappers').hide()
      $('.scrollDiv').removeClass('addAiBoxOpen')
    }
  }

  return (
    <div className="fiveDays">
      {enableComp && (
        <div className="cell hoverDiv">
          <div
            className={`headingCell ${HighlightPresent}${
              TagChek != '' ? ' headingCellWs' : ''
            }`}
            style={styleObj}
          >
            {calView == 'month'
              ? list
              : moment(list, 'YYYY-MM-DD').format('ddd MMM D')}
          </div>
          <div
            className="addAI addAIArea clickable addAIButton"
            onClick={(e) => addAIButton(e, list, calView)}
            data-dummy-url-data={dummyUrl}
            data-url={createAI}
            style={{ bordeLeft: 'none', visibility: 'visible' }}
          />
          <AddAiDescForm
            actions={actions}
            list={list}
            formatDate={formatDate}
            formatStartDate={formatStartDate}
            calView={calView}
            currentUserId={currentUserId}
            tagId={props.myworkData.tagId}
            addAIButton={addAIButton}
            workstreamId={workstreamId}
            teamView={teamView}
            workstreamName={workstreamName}
            ref={textareaRef}
          />
          <div
            id={`connectedSortable-${col}`}
            className="scrollDiv  connectedSortableParent"
            style={{ minHeight: '800px' }}
            data-enable-drag="1"
            data-due-date={list}
            data-view={calView}
            data-calview={calViewDataRangeVal}
            data-teamview={teamView}
            data-orgtagid={orgTagId}
            data-teamid={teamId}
            data-tag={TagChek}
          >
            {typeof meetingDays[list] === 'object' &&
              Object.keys(meetingDays[list]).length > 0 &&
              meetEnable &&
              Object.keys(meetingDays[list]).map((meet, index) => (
                <MeetingCard
                  key={index}
                  meetingDays={meetingDays[list][meet]}
                  keys={list}
                  currentUserId={currentUserId}
                  offsetTimzone={offsetTimzone}
                  loggedInUser={loggedInUser}
                  list={list}
                  showMeeting={props.showMeeting}
                />
              ))}
            {HighlightPresent !== '' &&
              cascadeList.map((kr) => (
                <CascadeKRCard kr={kr} key={kr.metricId} t={t} />
              ))}
            <div
              id={`sortable${col}`}
              data-enable-drag="1"
              className=" connectedSortable weekdays  "
              data-view={calView}
              data-calview={calViewDataRangeVal}
              calviewdatarange={calViewDataRangeVal}
              date-range={list}
              data-due-date={list}
              style={{ minHeight: '800px', overflowX: 'hidden' }}
            >
              {typeof dataAiList === 'object' &&
                dataAiList.length > 0 &&
                dataAiList.map((item) => (
                  <AiCard
                    key={item.id}
                    dataAiList={item}
                    calViewDataRange={calViewDataRange}
                    keys={keys}
                    calView={calView}
                    TagChek={TagChek}
                    teamView={teamView}
                    editAIURL={editAIURL}
                    isTeamManager={isTeamManager}
                    list={list}
                    currentUserId={currentUserId}
                    offset={offset}
                    menuKeepOpen={menuKeepOpen}
                    editAiSmallWindow={editAiSmallWindow}
                    actions={actions}
                    myWorkData={props.myworkData}
                    col={col}
                    thisWeekAiPref={props.myworkData.thisWeekAiPref}
                    teamId={props.myworkData.teamId}
                    showSubactions={props.showSubactions}
                    aiStatus={props.aiStatus}
                    aiRating={props.aiRating}
                    aiPriority={props.aiPriority}
                    aiWorkstreamIds={props.aiWorkstreamIds}
                    aiTagIds={props.aiTagIds}
                  />
                ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

/*
  This week Meeting component MeetingCard
*/
const MeetingCard = memo((props) => {
  // const currentDateTime = () => {
  // 	const m = moment.utc();
  // 	let timestamp = m.format("X");
  // 	const offset = 0;
  // 	timestamp = parseInt(timestamp) + offset;
  // 	return moment.utc(timestamp * 1000).format("g:ia");
  // }

  const openMeetingPage = (e, meetingId, status, type) => {
    if (meetingId !== 'undefined') {
      const currentAddressBarUrl = `${window.location.href}?from_where=msteams`
      if (
        typeof currentAddressBarUrl !== 'undefined' &&
        currentAddressBarUrl.indexOf('from_where=msteams') != -1
      ) {
        const meetingUrl = `${wbConfig.baseUrl}/meeting/meetingDetails?id=${meetingId}`
        window.open(meetingUrl, '_blank')
      } else {
        triggerLeftMenu(
          `${wbConfig.baseUrl}/meeting/meetingDetails?id=${meetingId}`,
          'menu-wb-mymeeting'
        )
        activeTopMenuId('meetings')
      }
    }
  }
  const meeting = props.meetingDays
  const { currentUserId } = props
  const backgroundColor = 'rgba(179, 157, 218, 0.4)'
  const leftColor = '4px solid #b39dda'
  let meetingText = ''
  if (meeting.type == 1) {
    meetingText = `Prep for 1on1 with ${
      meeting.owner == currentUserId ? meeting.memberName : meeting.ownerName
    }`
  } else {
    meetingText = meeting.name
  }
  return (
    <div
      className={
        props.showMeeting
          ? 'headingCell meeting-calendar-cell clickable'
          : 'headingCell meeting-calendar-cell clickable hide'
      }
      style={{ borderLeft: leftColor, background: backgroundColor }}
      onClick={(e) =>
        openMeetingPage(e, meeting.id, meeting.status, meeting.type)
      }
    >
      {meetingText}
      <div className="meetingTimeText">
        {moment.utc(meeting.sched_at_with_offset * 1000).format('h:mm a')}
      </div>
    </div>
  )
})

/*
 * Create a component for this week each AI attributes
 */
const AiAttrComp = (props) => {
  const { dataAiList, privateAi, TagChek } = props
  const { attributes } = getAiTypeAttributes(dataAiList.type)
  const stateCompName = getStateComponentIcon(
    attributes?.state?.values,
    dataAiList.state
  )
  const priorityCompName = getStateComponentIcon(
    attributes?.priority?.values,
    dataAiList.priority
  )
  const emptyTag = TagChek == ''
  return (
    <div className="aiAttrBlock">
      {privateAi && emptyTag && (
        <div
          className="aiAttr pull-left privateAiAttr"
          style={{ width: '17px' }}
        >
          &nbsp;{' '}
        </div>
      )}
      {dataAiList.parent != '' && dataAiList.parent != null && emptyTag && (
        <div
          className="subActionLinkThisWeek pull-left aiAttr"
          style={{ marginBottom: '0px', marginTop: '3px' }}
        >
          &nbsp;{' '}
        </div>
      )}
      <AiAttrIconWrapper>
        <Icon name={priorityCompName} width="15" height="15" />
      </AiAttrIconWrapper>
      {emptyTag && dataAiList.state != 0 && (
        <AiAttrIconWrapper>
          <Icon name={stateCompName} width="15" height="15" />
        </AiAttrIconWrapper>
      )}
      {emptyTag && (
        <div
          className="aiAttr wbWeekChecklist pull-left"
          style={{
            width: '10%',
            padding: '3px 5px 0 0',
            display:
              dataAiList.checklistsCount == null ||
              dataAiList.checklistsCount == ''
                ? 'none'
                : '',
          }}
        >
          <div className="wbViewChecklistImg">&nbsp;</div>
        </div>
      )}
      {emptyTag && (
        <div
          title=""
          className="aiAttr wbViewComment pull-left"
          data-status=""
          style={{
            width: '20%',
            display: dataAiList.comments > 0 ? '' : 'none',
          }}
        >
          <div className="wbActCommentCnt displayCell">
            {' '}
            {dataAiList.comments > 0 ? dataAiList.comments : ''}{' '}
          </div>
          <div className="wbViewCommentsImg displayCell">&nbsp;</div>
        </div>
      )}
      {emptyTag && (
        <div
          title=""
          className="aiAttr wbViewAttach pull-left"
          style={{
            width: '20%',
            display: dataAiList.AttachCount > 0 ? '' : 'none',
          }}
          data-status="1"
        >
          <div className="wbAttachmentCount displayCell">
            {dataAiList.AttachCount > 0 ? dataAiList.AttachCount : ''}
          </div>
          <div className="wbViewAttachmentImg displayCell">&nbsp;</div>
        </div>
      )}
      {emptyTag && (
        <div
          title=""
          className="aiAttr wbViewAttach pull-left"
          style={{
            width: '14%',
            display: dataAiList.tagCount > 0 ? '' : 'none',
          }}
          data-status="1"
        >
          <div className="wbAttachmentCount displayCell">
            {dataAiList.tagCount > 0 ? dataAiList.tagCount : ''}
          </div>
          <div className="wbViewTagImg displayCell">&nbsp;</div>
        </div>
      )}
    </div>
  )
}

/*
 * Create a component for this week for last column
 */
const ThisWeekNextColum = (props) => {
  const {
    actions,
    myworkData,
    constantCheck,
    editAiSmallWindow,
    showMeeting,
    showSubactions,
    aiStatus,
    aiRating,
    aiPriority,
    aiWorkstreamIds,
    aiTagIds,
  } = props
  const {
    calViewDataRange,
    calView,
    teamView,
    meetingDays,
    currentUserId,
    offsetTimzone,
    loggedInUser,
    menuKeepOpen,
  } = myworkData
  const dataAis = props.dateAis
  const sortArr = myworkData.sorting
  return (
    <div className="weekEndDays">
      {Object.keys(dataAis).length > 0 &&
        Object.keys(dataAis).map(
          (item, index) =>
            index > 4 && (
              <ThisweekLastTwoComp
                myworkData={myworkData}
                dateAi={sortAis(dataAis[item], getColSortArr(sortArr, item))}
                keys={index}
                list={item}
                calView={calView}
                calViewDataRange={calViewDataRange}
                TagChek={myworkData.tagId}
                teamView={teamView}
                meetingDays={meetingDays}
                currentUserId={currentUserId}
                offsetTimzone={offsetTimzone}
                loggedInUser={loggedInUser}
                constantCheck={constantCheck}
                editAiSmallWindow={editAiSmallWindow}
                actions={actions}
                menuKeepOpen={menuKeepOpen}
                key={index}
                showMeeting={showMeeting}
                showSubactions={showSubactions}
                aiStatus={aiStatus}
                aiRating={aiRating}
                aiPriority={aiPriority}
                aiWorkstreamIds={aiWorkstreamIds}
                aiTagIds={aiTagIds}
                day={index}
              />
            )
        )}
    </div>
  )
}

/*
  This week main component ThisweekLastTwoComp
*/
const ThisweekLastTwoComp = (props) => {
  const textareaRef = useRef(null);
  useEffect(() => {
    // Anything in here is fired on component mount.
    document.body.addEventListener('click', bodyClickHandler, true)
    return () => {
      // Anything in here is fired on component unmount.
      document.body.removeEventListener('click', bodyClickHandler, true)
    }
  }, [])

  const addAIButton = (e, listed) => {
    $('.wsBoardAddAiWrappers').hide()
    $('.scrollDiv').removeClass('addAiBoxOpen')
    $(`.wsBoardAddAiWrappers.this-week-aiform-${listed}`).show()
    $(`.wsBoardAddAiWrappers.this-week-aiform-${listed}`)
      .next('.scrollDiv')
      .addClass('addAiBoxOpen')
    $(`.this-week-aiform-${listed} .thisWeekAddAi `).trigger('focus');
    // Focus the textarea
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }

  const bodyClickHandler = (e) => {
    if (
      !$('.addAIArea').is(e.target) && // if the target of the click isn't the container...
      $('.addAIArea').has(e.target).length != 0
    ) {
      $('.wsBoardAddAiWrappers').hide()
      $('.scrollDiv').removeClass('addAiBoxOpen')
    }
  }
  const dataAiList = props.dateAi
  const {
    actions,
    constantCheck,
    list,
    calViewDataRange,
    keys,
    calView,
    TagChek,
    teamView,
    meetingDays,
    offsetTimzone,
    aiWorkstreamIds,
    showSubactions,
    aiStatus,
    aiRating,
    aiPriority,
    aiTagIds,
    editAiSmallWindow,
  } = props
  const {
    currentUserId,
    workstreamName,
    workstreamId,
    imageCdnUrl,
    editAIURL,
    createUrl,
    orgTagId,
    loggedInUser,
    teamId,
    isTeamManager,
  } = props.myworkData
  const arrowNext = `${imageCdnUrl}/images/arrow_right.png`
  const urlField4 =
    TagChek != '' && TagChek != 'team' ? `&wsId=${orgTagId}` : ''
  const urlField5 = TagChek == 'team' ? `&teamId=${TagChek}&teamView=1` : ''
  const createAI = `${createUrl}?uid=${loggedInUser}&actionType=createAI&aiDueDate=${moment(
    list,
    'YYYY-MM-DD'
  ).format('YYYY-MM-DD')}${urlField4}${urlField5}`
  const dummyUrl = `&uid=${loggedInUser}&actionType=createAI&aiDueDate=${moment(
    list,
    'YYYY-MM-DD'
  ).format('YYYY-MM-DD')}${urlField4}${urlField5}`
  const weekNextDate = !!(keys == constantCheck && TagChek == '')
  const styleObj = { marginRight: '-2px' }
  if (keys == constantCheck) {
    styleObj.textAlign = 'center'
    styleObj.backgroundColor = 'rgba(204, 204, 204, 0.15)'
  }
  if (keys == 6 && TagChek == '') {
    styleObj.height = '15px'
    styleObj.borderTop = '1px solid #E6E5E5'
    styleObj.borderBottom = '1px solid #E6E5E5'
    styleObj.marginTop = '0px'
    styleObj.backgroundColor = 'rgba(204, 204, 204, 0.15)'
  }
  const colId = keys + 1
  let styleObject = {}
  if (TagChek != '' && keys > 4) {
    styleObject = { overflowX: 'hidden' }
    // styleObject = {overflowX:'hidden',overflowY:'hidden'};
  } else {
    styleObject = { overflowX: 'hidden' }
    // styleObject = {overflowX:'hidden',overflowY:'hidden'};
  }
  let workmonth = ''
  if (calView != 'month' && keys == 5) {
    workmonth = 'hoverDiv'
  }
  let workstreammonth = ''
  if (TagChek != '' && keys == 6) {
    workstreammonth = <div className="divider" />
  }
  const col = keys + 1
  let formatDate = list
  let formatStartDate = list
  if (calView == 'month') {
    const str_array = list.split('-')
    const monthStartDate = str_array[0].trim()
    const monthendDate = str_array[1].trim()
    formatDate = moment(monthendDate, 'MMM DD').format('YYYY-MM-DD')
    formatStartDate = moment(monthStartDate, 'MMM DD').format('YYYY-MM-DD')
  }
  const UserTimeZone =
    props.myworkData.userTimeZone != 'undefined' &&
    props.myworkData.userTimeZone != null
      ? props.myworkData.userTimeZone.timezone
      : null
  const presentDate = new Date()
  let HighlightPresent = ''
  if (
    UserTimeZone &&
    UserTimeZone != null &&
    moment(presentDate).tz(UserTimeZone).format('YYYY-MM-DD') == list
  ) {
    HighlightPresent = 'headingCellWsCurrent'
    styleObj.backgroundColor = '#52bad5'
    styleObj.color = '#fff'
    styleObj.fontWeight = '#bold'
  } else {
    styleObj.backgroundColor = 'rgba(204, 204, 204, 0.15)'
  }
  const cascadeList = props.myworkData.cascadeList || []
  return (
    <div>
      {workstreammonth}
      <div
        className={` cell ${TagChek != '' ? workmonth : 'altWidth'}`}
        style={{ minHeight: TagChek != '' && keys == 5 ? '150px' : 'auto' }}
      >
        <div className="hoverDiv" style={{ height: 'auto' }}>
          <div
            className={`headingCell HighlightPresent${
              TagChek != '' ? 'headingCellWs' : ''
            }`}
            style={styleObj}
            id={`headingCell${colId}`}
          >
            {moment(list, 'YYYY-MM-DD').format('ddd MMM D')}
          </div>
          <div
            className="addAI addAIArea clickable addAIButton"
            onClick={(e) => addAIButton(e, list)}
            data-dummy-url-data={dummyUrl}
            data-url={createAI}
            style={{ bordeLeft: 'none', visibility: 'visible' }}
          />
          <AddAiDescForm
            actions={actions}
            list={list}
            calView={calView}
            formatDate={formatDate}
            formatStartDate={formatStartDate}
            currentUserId={currentUserId}
            tagId={props.myworkData.tagId}
            addAIButton={addAIButton}
            workstreamId={workstreamId}
            teamView={teamView}
            workstreamName={workstreamName}
            ref={textareaRef}
          />
          <div
            id={`connectedSortable-${col}`}
            className="scrollDiv  connectedSortableParent"
            style={{ minHeight: '85px' }}
            data-enable-drag="1"
            data-due-date={list}
            data-view={calView}
            data-teamview={teamView}
            data-orgtagid={orgTagId}
            data-teamid={teamId}
            data-tag={TagChek}
          >
            {typeof meetingDays[list] === 'object' &&
              Object.keys(meetingDays[list]).length > 0 &&
              Object.keys(meetingDays[list]).map((meet) => (
                <MeetingCard
                  meetingDays={meetingDays[list][meet]}
                  keys={list}
                  currentUserId={currentUserId}
                  offsetTimzone={offsetTimzone}
                  loggedInUser={loggedInUser}
                  list={list}
                  showMeeting={props.showMeeting}
                  key={`this_week_meeting_${meet.id}`}
                />
              ))}
            {HighlightPresent !== '' &&
              cascadeList.map((kr) => (
                <CascadeKRCard kr={kr} key={kr.metricId} t={t} />
              ))}
            <div
              id={`sortable${col}`}
              data-enable-drag="1"
              className=" connectedSortable weekends"
              data-due-date={list}
              data-view={calView}
              style={styleObject}
            >
              {typeof dataAiList === 'object' &&
                dataAiList.length > 0 &&
                dataAiList.map((item, index) => (
                  <AiCard
                    dataAiList={item}
                    key={item.id}
                    calView={calView}
                    TagChek={TagChek}
                    teamView={teamView}
                    editAIURL={editAIURL}
                    isTeamManager={isTeamManager}
                    list={list}
                    editAiSmallWindow={editAiSmallWindow}
                    actions={props.actions}
                    myWorkData={props.myworkData}
                    menuKeepOpen={props.menuKeepOpen}
                    thisWeekAiPref={props.myworkData.thisWeekAiPref}
                    currentUserId={currentUserId}
                    col={col}
                    teamId={teamId}
                    showSubactions={showSubactions}
                    aiStatus={aiStatus}
                    aiRating={aiRating}
                    aiPriority={aiPriority}
                    aiWorkstreamIds={aiWorkstreamIds}
                    aiTagIds={aiTagIds}
                  />
                ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const AddAiDescForm = forwardRef((props, ref) => {
  const {
    list,
    actions,
    currentUserId,
    calView,
    formatDate,
    formatStartDate,
    workstreamId,
    teamView,
    workstreamName,
  } = props
  let wrapperClass = ''
  if (calView == 'month') {
    $(`.this-week-aiform-${formatDate} .thisWeekAddAi `).trigger('focus')
    wrapperClass = `wsAiAddNote this-week-aiform-${formatDate}`
  } else {
    $(`.this-week-aiform-${formatDate} .thisWeekAddAi `).trigger('focus')
    wrapperClass = `twAiAddNote this-week-aiform-${list}`
  }

  const onSubmitAi = (e) => {
    const desc = e.target.value.trim()
    if (
      ((e.keyCode == 13 && e.shiftKey == false) || e.type == 'blur') &&
      desc != ''
    ) {
      const randNum = Math.floor(Math.random() * 10000000) + 1
      const tempAiId = `newAi_${randNum}`
      const stateText = WbEventHandlerMixins.getStateText(1)
      let startTimeStamp = ''
      let addAiDue = ''
      let dueDateFormat = ''
      let startDate = ''
      if (calView == 'month') {
        startTimeStamp = moment(formatStartDate, 'YYYY-MM-DD').format(
          'MMM DD,YYYY'
        )
        addAiDue = moment(formatDate, 'YYYY-MM-DD').format('MMM DD ‘YY')
        dueDateFormat = new Date(formatDate).getTime() / 1000
        startDate = new Date(formatStartDate).getTime() / 1000
      } else {
        startTimeStamp = moment(list, 'YYYY-MM-DD').format('MMM DD,YYYY')
        addAiDue = moment(list, 'YYYY-MM-DD').format('MMM DD ‘YY')
      }

      const tempAiArr = []
      tempAiArr.id = tempAiId
      tempAiArr.description = desc
      tempAiArr.dueDateText = startTimeStamp
      if (calView == 'month') {
        tempAiArr.formatDate = formatDate
        tempAiArr.list = list
        tempAiArr.due_date = startDate
        // tempAiArr['start_date'] = startDate;
        // tempAiArr['lateDate'] = 0;

        const now = moment()
        const input = moment(formatStartDate)

        if (now.isoWeek() == input.isoWeek()) {
          tempAiArr.lateDate = 0
        } else if (now.isoWeek() > input.isoWeek()) {
          if (startDate < dueDateFormat) {
            tempAiArr.lateDate = 1
          } else {
            tempAiArr.lateDate = 0
          }
        } else if (now.isoWeek() < input.isoWeek()) {
          tempAiArr.lateDate = 0
        } else {
          tempAiArr.lateDate = 0
        }
      } else {
        tempAiArr.list = list
      }
      tempAiArr.privateAi = false
      tempAiArr.parentId = null
      if (teamView == '1' && workstreamId != '') {
        tempAiArr.board = workstreamName
      } else {
        tempAiArr.board = 'Personal Stream'
      }
      tempAiArr.canEditAI = true
      tempAiArr.effort = 1
      tempAiArr.priority = 3
      tempAiArr.rag = 0
      tempAiArr.calView = calView
      const userId = props.currentUserId
      // props.addAIButton(e,list);
      if (calView == 'month') {
        $(`.this-week-aiform-${formatDate}`).hide()
        $(`.this-week-aiform-${formatDate}`)
          .next('.scrollDiv')
          .removeClass('addAiBoxOpen')
      } else {
        $(`.this-week-aiform-${list}`).hide()
        $(`.this-week-aiform-${list}`)
          .next('.scrollDiv')
          .removeClass('addAiBoxOpen')
      }

      const { description } = tempAiArr
      let res = description.replace('<', '&lt;')
      res = res.replace('>', '&gt;')
      tempAiArr.description = res

      actions.MyworkAiAddNew(tempAiArr)
      const params = {
        JQ: '',
        xstate: stateText,
        thisWeekSec: 1,
        aiDetEffort: 1,
        xrag: 'green',
        priority: 3,
        wsBoardView: 1,
        description: desc,
        addAiDueText: addAiDue,
      }

      if (teamView == '1' && workstreamId != '') {
        params.board = workstreamId
      } else {
        params.board = props.tagId != '' ? props.tagId : -11
      }
      params.assign = currentUserId
      params.due_date = startTimeStamp

      e.target.value = ''
      if ($.ajaxSetup().data.YII_CSRF_TOKEN) {
        params.YII_CSRF_TOKEN = $.ajaxSetup().data.YII_CSRF_TOKEN
      }
      params.uid = userId
      $.post(
        `${wbConfig.baseUrl}/activity/create`,
        params,
        (response, status) => {
          if (status == 'success') {
            if (typeof myworkCount === 'function') {
              myworkCount()
            }
            setTimeout(
              () => actions.MyworkAiUpdate(tempAiId, list, response.data),
              200
            )
          }
        }
      )
      if (getMaxHeight('.scrollDiv') > 800) {
        $('.scrollDiv').height('auto')
        $('.divider').height(
          getMaxHeight('.scrollDiv') + getMaxHeight('#sortable7') + 300
        )
      }

      if (
        getMaxHeight('#thisWeekAIs') > 800 &&
        (calView == 'month' || calView == 'week')
      ) {
        $('.cell').height('auto')
        $('.divider').height(getMaxHeight('.cell') + 300)
      }
    } else {
      return false
    }
  }
  return (
    <div
      className={`wsBoardAddAiWrappers ${wrapperClass}`}
      style={{ display: 'none' }}
    >
      <textarea
        name="description"
        className="thisWeekAddAi"
        onBlur={onSubmitAi}
        onKeyDown={onSubmitAi}
        autoFocus
        ref={ref}
      />
    </div>
  )
});

class EditAiSmallPopUp extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      showListPopup: '',
      aiAssigniList: [],
      leftSpace: '',
      topSpace: '',
      enableEff: false,
    }
    this.startDateClick = this.startDateClick.bind(this)
    this.dueDateClick = this.dueDateClick.bind(this)
    this.remindMeAiClick = this.remindMeAiClick.bind(this)
    this.saveReminder = this.saveReminder.bind(this)
    this.saveStartDate = this.saveStartDate.bind(this)
    this.saveEndDate = this.saveEndDate.bind(this)
    this.updateStatusValue = this.updateStatusValue.bind(this)
    this.showEffortPopUp = this.showEffortPopUp.bind(this)
    this.updateEffortValue = this.updateEffortValue.bind(this)
    this.updateRagValue = this.updateRagValue.bind(this)
    this.updatePriorityValue = this.updatePriorityValue.bind(this)
    this.deleteAI = this.deleteAI.bind(this)
    this.onShowListPopup = this.onShowListPopup.bind(this)
    this.hideShowListPopup = this.hideShowListPopup.bind(this)
    this.dueDatePicker = React.createRef()
    this.startDatePicker = React.createRef()
    this.cleanDate = this.cleanDate.bind(this)
    this.clickOutside = this.clickOutside.bind(this)
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.clickOutside)
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.clickOutside)
  }

  clickOutside(e) {
    const element = e.target
    const node = ReactDOM.findDOMNode(this.dueDatePicker.current)
    if (
      this.dueDatePicker &&
      this.dueDatePicker.current &&
      !node.contains(element)
    ) {
      this.dueDatePicker.current.hideDayPicker()
    }
    const node1 = ReactDOM.findDOMNode(this.startDatePicker.current)
    if (
      this.startDatePicker &&
      this.startDatePicker.current &&
      !node1.contains(element)
    ) {
      this.startDatePicker.current.hideDayPicker()
    }
  }

  startDateClick() {
    this.dueDatePicker.current.hideDayPicker()
    this.startDatePicker.current.showDayPicker()
  }

  dueDateClick() {
    this.startDatePicker.current.hideDayPicker()
    this.dueDatePicker.current.showDayPicker()
  }

  remindMeAiClick() {
    const _this = this
    const { id } = this.props.dataAiList
    const obj = $(`#remindMiniEdit${id}`)
    $(obj)
      .datetimepicker({
        startDate: new Date(),
        minDate: new Date(),
        dateFormat: 'yy-mm-dd',
        timeFormat: 'hh:mm tt',
        controlType: 'select',
        currentText: 'Cancel',
        closeText: 'Set',
        beforeShow(input, inst) {
          $('#ui-datepicker-div').addClass(
            'dataTimePicker thisweekdatetimepicker'
          )
        },
        onClose(date) {
          function isDonePressed() {
            return (
              $('#ui-datepicker-div')
                .html()
                .indexOf(
                  'ui-datepicker-close ui-state-default ui-priority-primary ui-corner-all ui-state-hover'
                ) > -1
            )
          }
          if (isDonePressed()) {
            $('.wbActPopup').hide()
            $(obj).closest('.editAIWrap').show()
            _this.saveReminder(
              $(obj),
              moment(date).format('YYYY-MM-DD hh:mm a')
            )
          }
        },
      })
      .datetimepicker('show')
  }

  saveReminder(obj, dateTime) {
    $(obj).closest('.editAIWrap').show()
    const { actions } = this.props
    const url = this.props.myWorkData.AddRemindMeActivity
    const actID = this.props.dataAiList.id
    actions.MyworkReminderActionItem(actID, dateTime)
    $.post(url, { aid: actID, dateAndTime: dateTime }, () => {}).done(
      (res) => {}
    )
  }

  saveStartDate(startDate) {
    const { actions, dataAiList, myWorkData } = this.props
    const { updateAIStartDate } = myWorkData
    const repeatValue = dataAiList.repeat
    const actID = dataAiList.id
    const obj = document.getElementById(`nactEditStartDate${actID}`)
    if (repeatValue == '1') {
      if (startDate == '') {
        const activityTypeName = getActivityTypeName(dataAiList.type);
        alert(`Start date cannot be blank for repeat ${activityTypeName}s.`)
        return
      }
      showConfirmationAlert(obj, startDate)
    } else {
      // check if start date is greater than end date
      let endDt = dataAiList.due_date
      endDt = new Date(endDt * 1000)
      endDt = moment(endDt).format('MMM DD,YYYY 23:59:59')
      const stateValue = dataAiList.state
      let startTimeStamp = 0
      if (startDate == '') {
        startDate = 'No Date'
        startTimeStamp = 0
      } else {
        startTimeStamp = new Date(startDate).getTime() / 1000
        endDt = new Date(endDt).getTime() / 1000
        startDate = moment(startDate).format('MMM DD,YYYY')
      }

      if (
        startTimeStamp != 0 &&
        endDt != 0 &&
        startTimeStamp > endDt &&
        stateValue == '1'
      ) {
        // removeLoaderWidget();
        ai_date_start_popup.dialogOpen()
        return false
      }

      const updateAll = typeof updateType !== 'undefined' ? updateType : 'no'
      actions.MyworkStartDateActionItem(actID, startDate)
      $.post(
        updateAIStartDate,
        { start_date: startDate, act_id: actID, updateAll },
        (data, status) => {}
      )
    }
  }

  saveEndDate(endDate) {
    const { actions, dataAiList, calView, myWorkData } = this.props
    const { updateAIDueDate } = myWorkData
    const repeatValue = dataAiList.repeat
    const actID = dataAiList.id
    const obj = document.querySelector(`#nactEditStartDate${actID}`)
    if (repeatValue == '1') {
      if (endDate == '') {
        const activityTypeName = getActivityTypeName(dataAiList.type);
        alert(`Due date cannot be blank for repeat ${activityTypeName}s.`)
      } else {
        showConfirmationAlert(obj, endDate)
      }
    } else {
      const updateAll = typeof updateType !== 'undefined' ? updateType : 'no'
      const stateValue = dataAiList.state
      const selectedDate =
        endDate !== '' ? new Date(endDate).getTime() / 1000 : 0
      const startMinDate = dataAiList.start_date
      const dateMiniDueStart = startMinDate || 0
      if (
        dateMiniDueStart != 0 &&
        selectedDate != 0 &&
        selectedDate < dateMiniDueStart &&
        stateValue == '1'
      ) {
        removeLoaderWidget()
        ai_date_due_popup.dialogOpen()
        return false
      }
      if (endDate == '') {
        endDate = 'No Date'
      } else {
        endDate = moment(endDate).format('MMM DD,YYYY')
      }
      const previousEndDate = dataAiList.due_date
      if (
        endDate &&
        endDate != 'No Date' &&
        moment(previousEndDate * 1000).format('MM DD YYYY') ==
          moment(endDate).format('MM DD YYYY')
      ) {
        $('.editAIWrap').hide()
        return false
      }
      let monthWeek = ''
      if (calView == 'month') {
        monthWeek = `${moment(endDate).weekday(0).format('MMM DD')} - ${moment(
          endDate
        )
          .weekday(6)
          .format('MMM DD')}`
      }
      actions.MyworkEndDateActionItem(actID, endDate, calView, monthWeek)
      $('.editAIWrap').hide()
      $.post(
        updateAIDueDate,
        { due_date: endDate, act_id: actID, updateAll },
        (data, status) => {}
      )
    }
  }

  updateStatusValue(e, id, enableStatus, oldState) {
    const { actions } = this.props
    const _this = e.target

    if (enableStatus == 'false') {
      return false
    }
    const state = id
    const obj = $(_this)
    const postUrl = this.props.myWorkData.updateAIStatus
    const actID = this.props.dataAiList.id
    if (oldState == 1 && (state == 2 || state == 4)) {
      $(obj)
        .closest('#editThisWeekAI')
        .find('#ai-start-date-optionedit .wbDatePickStart')
        .html('No Date')
      $(obj)
        .closest('#editThisWeekAI')
        .find(`#nactEditStartDate${actID}`)
        .val('')
      actions.MyworkStartDateActionItem(actID, null)
    } else if (state == 1) {
      const currentTime = formatDateJs(true)
      const startDateVal = $(this)
        .closest('#editThisWeekAI')
        .find(`#nactEditStartDate${actID}`)
        .val()
      const startDate =
        startDateVal != '' && startDateVal != 'No Date'
          ? new Date(startDateVal)
          : new Date(formatDateJs(undefined, true))
      const endDate = $(this)
        .closest('#editThisWeekAI')
        .find(`#nactEditDueDate${actID}`)
        .val()
      const targetDate =
        endDate != '' && endDate != 'No Date' ? new Date(endDate) : 0
      if (targetDate != 0 && startDate > targetDate) {
        ai_date_due_popup.dialogOpen()
        return false
      }
      if (startDateVal == '' || startDateVal == 'No Date') {
        $(obj)
          .closest('#editThisWeekAI')
          .find('#ai-start-date-optionedit .wbDatePickStart')
          .html(currentTime)
        $(obj)
          .closest('#editThisWeekAI')
          .find(`#nactEditStartDate${actID}`)
          .val(currentTime)
      }
    }
    $('.statusVal').css('background-color', '#fff')
    $(obj).css('background-color', '#e2f3f8')
    if (state == 0) {
      $(obj).find('.editAIWrap').hide()
      $('.editAIWrap').hide()
    }
    let fromWs = 0
    if (this.props.TagChek) {
      fromWs = 1
    }
    actions.MyworkStatusActionItem(actID, state, fromWs)
    $.post(postUrl, { state, act_id: actID }, (data, status) => {
      myworkCount()
      $(`#aiBlock_${actID}`).find('.wbStatus').attr('data-status', state)
      if ($(obj).closest('.subActionItem').length) {
        if (state == 0) {
          $(obj)
            .closest('.subActionItem')
            .find('.subTaskText')
            .addClass('lineThrough')
          $(obj)
            .closest('.subActionItem')
            .find('.aiEditSubActionStatus .statusImg')
            .attr('data-value', state)
          $(obj)
            .closest('.subActionItem')
            .find(`#aiSubTask_${actID}`)
            .prop('checked', true)
          $(obj)
            .closest('.subActionItem')
            .find('.aiEditSubActionAttrDiv .statusImg')
            .attr('data-value', state)
          $(obj)
            .closest('.subActionItem')
            .find('.aiEditSubActionAttrDiv .statusImg')
            .hide()
          if (
            typeof updateSubActionCount !== 'undefined' &&
            $.isFunction(updateSubActionCount) &&
            oldState != state
          ) {
            updateSubActionCount('subActDoneCntText', 'add')
          }
        } else {
          $(obj)
            .closest('.subActionItem')
            .find('.subTaskText')
            .removeClass('lineThrough')
          $(obj)
            .closest('.subActionItem')
            .find('.aiEditSubActionStatus .statusImg')
            .attr('data-value', state)
          $(obj)
            .closest('.subActionItem')
            .find(`#aiSubTask_${actID}`)
            .prop('checked', false)
          $(obj)
            .closest('.subActionItem')
            .find('.aiEditSubActionAttrDiv .statusImg')
            .attr('data-value', state)
          $(obj)
            .closest('.subActionItem')
            .find('.aiEditSubActionAttrDiv .statusImg')
            .show()
          if (
            typeof updateSubActionCount !== 'undefined' &&
            $.isFunction(updateSubActionCount) &&
            oldState != state &&
            oldState == 0
          ) {
            updateSubActionCount('subActDoneCntText', 'delete')
          }
        }

        $(e.target).find('.editAIWrap').hide()
        $(obj).closest('.subActionItem').find('.subActionEditPopup').hide()
        backendLoadAfterSaveWithTimeOut(actID)
      }
    })
  }

  showEffortPopUp(e, enableEffort, effortVal) {
    if (enableEffort != 'true') {
      return false
    }
    this.setState({
      enableEff: !this.state.enableEff,
    })
    const _this = e.target.closest('.mini-popup-effort-dropdown')
    $('.wbActPopup').hide()
    const offset = $(_this).position()
    $(_this)
      .siblings('.miniEditEffortLists ')
      .show()
      .css({
        'margin-top': 0,
        top: offset.top + 43,
        left: offset.left - 40,
        'z-index': 100,
        position: 'absolute',
      })
  }

  updateEffortValue(e, effort) {
    const { actions } = this.props
    const postUrl = this.props.myWorkData.changeEffortUrl
    const actID = this.props.dataAiList.id
    this.setState({
      enableEff: !this.state.enableEff,
    })
    const _this = e.target
    actions.MyworkEffortActionItem(actID, effort)
    $.post(postUrl, { effort, act_id: actID }, (data, status) => {})
    $(_this).closest('.miniEditEffortLists').hide()
  }

  updateRagValue(e, rag, enableRag) {
    const { actions } = this.props
    const postUrl = this.props.myWorkData.updateAIRag
    const actID = this.props.dataAiList.id
    if (enableRag == 'false') {
      return
    }
    $('.miniEditVrtArrow').hide()
    $('.miniEditAssigniWrap').hide()
    actions.MyworkRatingActionItem(actID, rag)
    $.post(postUrl, { rag, act_id: actID }, (data, status) => {})
  }

  updatePriorityValue(e, priority, enablePriority) {
    const { actions } = this.props
    const postUrl = this.props.myWorkData.updateAIPriority
    const actID = this.props.dataAiList.id
    if (enablePriority == 'false') {
      return false
    }
    $('.miniEditVrtArrow').hide()
    $('.miniEditAssigniWrap').hide()
    if (priority != 3) {
      $(`#aiBlock_${actID}`)
        .find('.wbPriority')
        .css({ width: '15%', 'margin-right': '5px' })
    }
    actions.MyworkPriorityActionItem(actID, priority)
    $.post(postUrl, { priority, act_id: actID }, (data, status) => {})
  }

  deleteAI(e, actId) {
    const _this = e.target
    let meetingId = false
    if ($(_this).closest('.meeting-agenda-items-wrapper').length) {
      if (
        $(_this)
          .closest('.meeting-agenda-items-wrapper')
          .find('#deleted_goalai_meeting_id').length
      ) {
        meetingId = $(_this)
          .closest('.meeting-agenda-items-wrapper')
          .find('#deleted_goalai_meeting_id')
          .val() // this only remove goal from meeting agenda items.
      }
    }
    this.props.handleDeleteAi({ actId, meetingId })
    $('.editAIWrap').hide()
  }

  onShowListPopup(e, view, access) {
    if (view && access) {
      const aiId = this.props.dataAiList.id
      this.setState({
        showListPopup: view,
      })
      const keepOpen = this.props.menuKeepOpen
      const obj1 = e.target
      const miniObj = $(`#editThisWeekActionItem_${aiId}`)
      const offset = $(miniObj).position()
      const offsetInput = $(obj1).position()
      let { left } = offset
      const leftDiv = offsetInput.left
      if (left > 750 && keepOpen === 'true') {
        left = leftDiv - 458
      } else if (left > 370 && keepOpen === 'true') {
        left = '-280'
      } else {
        left = 0
      }
      if (navigator.appVersion.indexOf('Mac') != -1) {
        left -= 40
      }
      const topPos = offset.top
      let top = ''
      if (!$('#mywork_tabs_container').length && !$('.WsTeamView').length) {
        if (topPos > -17) {
          top = -168
        }
      } else if ($('.WsTeamView').length) {
        if (topPos > 30) {
          top = -168
        }
      }
      this.setState({
        leftSpace: left,
        topSpace: top,
      })
      $('.wsBoardOwnerListWrapperDiv').show()
    }
  }

  hideShowListPopup() {
    this.setState({
      showListPopup: '',
    })
    $('.editAIWrap').hide()
  }

  cleanDate(obj, type = '') {
    if (obj) {
      obj.current.hideDayPicker()
      if (type == 'due_date') {
        this.saveEndDate('')
      } else if (type == 'start_date') {
        this.saveStartDate('')
      }
    }
  }

  render() {
    const { dataAiList, boxStyle, t } = this.props
    let fromText = ''
    if (dataAiList.parent != '' && dataAiList.fromAction != '') {
      fromText = '&fromParentAI=true'
    }
    if (dataAiList.fullReload != '') {
      fromText += '&fullReload=1'
    }
    const aiDetailsUrl = `${this.props.myWorkData.ActionItemDetails}?id=${
      dataAiList.id
    }&aiDueDate=${moment(dataAiList.due_date * 1000).format(
      'YYYY-MM-DD'
    )}${fromText}`
    let parentDesc = false
    let parentDescText = dataAiList.description
    let aiDetailsUrl1 = `${this.props.myWorkData.ActionItemDetails}?id=${
      dataAiList.id
    }&aiDueDate=${moment(dataAiList.due_date * 1000).format('YYYY-MM-DD')}`
    if (
      dataAiList.parent != '' &&
      dataAiList.parent != null &&
      dataAiList.parentModel != null
    ) {
      parentDesc = true
      parentDescText = dataAiList.parentModel.description
      aiDetailsUrl1 = `${this.props.myWorkData.ActionItemDetails}?id=${
        dataAiList.parentModel.id
      }&aiDueDate=${moment(dataAiList.due_date * 1000).format('YYYY-MM-DD')}`
    }

    let startDateText = ''
    let startDateValue = ''
    const isInvalidStartDate =
      dataAiList.start_date == '' ||
      dataAiList.start_date == null ||
      isNaN(dataAiList.start_date) ||
      dataAiList.start_date == 0
    if (isInvalidStartDate) {
      startDateText = 'No Date'
      startDateValue = ''
    } else {
      const startDateMoment = moment(dataAiList.start_date * 1000).utc()
      startDateText = startDateMoment.format('MMM D')
      startDateValue = startDateMoment.format('MMM DD, YYYY')
    }
    let dueDateText = ''
    let dueDateValue = ''
    if (dataAiList.due_date == '' || dataAiList.due_date == null) {
      dueDateText = 'None'
      dueDateValue = ''
    } else {
      const dueDateMoment = moment(dataAiList.due_date * 1000).utc()
      dueDateText = dueDateMoment.format('MMM D')
      dueDateValue = dueDateMoment.format('MMM DD, YYYY')
    }
    const reminderStyle = ''
    const { reminderData } = dataAiList

    const enablDeleteSec =
      dataAiList.isTeamManagers ||
      dataAiList.owner === dataAiList.loggedInUserId
    let boxMarginLeft = '0px'
    const popupWidth = 291
    if (window.innerWidth - boxStyle.right < popupWidth) {
      boxMarginLeft = '-115px'
    }
    const startDateDisabledDays = {}
    let endDateDisabledDays = {}
    if (!isInvalidStartDate) {
      const startDateString = moment(
        dataAiList.start_date * 1000 + 86400000
      ).toDate()
      endDateDisabledDays = {
        month: startDateString,
        fromMonth: startDateString,
        disabledDays: {
          before: startDateString,
        },
      }
    }

    const typeAttributes = getAiTypeAttributes(dataAiList.type)
    const { attributes } = typeAttributes

    return (
      <div
        id={`editThisWeekActionItem_${dataAiList.id}`}
        className="editAIWrap dialogbox-shadow"
        data-attr={dataAiList.id}
        style={{
          zIndex: '20',
          position: 'absolute',
          marginLeft: boxMarginLeft,
        }}
      >
        <div
          id="editThisWeekAI"
          className="editThisWeekAI WbActRow"
          data-repeatvalue={dataAiList.repeat}
          modeltype=""
          activity-id={dataAiList.id}
          can-add-non-member={dataAiList.canAddNonMember}
          data-nonmember={dataAiList.canAddNonMember}
          data-teamid={dataAiList.teamIds}
        >
          <input
            type="hidden"
            className=""
            name="activityId"
            id="activityId"
            value={dataAiList.id}
            readOnly
          />
          <div className="meAiBlock">
            <div
              className="aiDesc truncate3 editThisWeekDesc"
              title={dataAiList.description}
              style={{ lineHeight: '20px' }}
            >
              <span
                className="editThisWeekDescLink ai-v1-popup"
                data-href={aiDetailsUrl}
                style={{ color: '#3DA2BC', cursor: 'pointer' }}
                dangerouslySetInnerHTML={{
                  __html: dataAiList.descriptionForSmallPopup,
                }}
              />
            </div>
            <div>
              <div
                className="miniEditWsTitle truncateText"
                title={dataAiList.ws_name}
                style={{ color: '#000' }}
              >
                {dataAiList.ws_name}
              </div>
              <div
                className="miniEditTeamName truncateText"
                title={dataAiList.teamNames}
              >
                : {dataAiList.teamNames}
              </div>
            </div>
            {parentDesc && (
              <div
                className="editThisWeekParentDetails truncateText"
                title={parentDescText}
                aria-describedby="ui-tooltip-13"
                data-parentdesc={parentDesc}
              >
                <span className="ai-v1-popup" data-href={aiDetailsUrl1}>
                  {t('Linked to')} {' '}
                  <span
                    className="editThisWeekParentDesc"
                    dangerouslySetInnerHTML={{ __html: parentDescText }}
                  />
                </span>
              </div>
            )}
            <div
              className="aiAttrBlock"
              style={{ height: 'auto', width: '100%' }}
            >
              <div
                className="wbViewComment"
                data-status=""
                style={{
                  float: 'left',
                  width: '40px',
                  display: dataAiList.cmtCount > 0 ? '' : 'none',
                }}
              >
                <div className="wbActCommentCnt displayCell">
                  {' '}
                  {dataAiList.cmtCount > 0 ? dataAiList.cmtCount : ''}{' '}
                </div>
                <div className="wbViewCommentsImg displayCell">&nbsp;</div>
              </div>
              <div
                className="wbViewAttach "
                style={{
                  float: 'left',
                  width: '40px',
                  display: dataAiList.AttachCount > 0 ? '' : 'none',
                }}
                data-status="1"
              >
                <div className="wbAttachmentCount displayCell">
                  {dataAiList.AttachCount > 0 ? dataAiList.AttachCount : ''}
                </div>
                <div className="wbViewAttachmentImg displayCell">&nbsp;</div>
              </div>
              <div
                className="wbViewAttach "
                style={{
                  float: 'left',
                  width: '40px',
                  display: dataAiList.tagCount > 0 ? '' : 'none',
                }}
                data-status="1"
              >
                <div className="wbAttachmentCount displayCell">
                  {dataAiList.tagCount > 0 ? dataAiList.tagCount : ''}
                </div>
                <div className="wbViewTagImg displayCell">&nbsp;</div>
              </div>
            </div>
          </div>
          <div className="displayTable" style={{ width: '100%' }}>
            <div
              className="displayCell wbStatusEdit"
              style={{
                paddingTop: '13px',
                fontSize: '11px',
                color: '#333',
                lineHeight: '16px',
                width: '75%',
              }}
            >
              {`${attributes?.state?.label ?? 'Status'}`}:
              <InlineStateIconBox
                name="state"
                values={attributes?.state?.values ?? []}
                value={dataAiList.state}
                isEnabled={dataAiList.disable}
                onClickEvent={this.updateStatusValue}
              />
            </div>
            <div
              className="displayCell miniEditEffort"
              act-id={dataAiList.id}
              style={{
                paddingTop: '13px',
                fontSize: '11px',
                color: '#333',
                lineHeight: '16px',
              }}
            >
              {`${attributes?.effort?.label ?? 'Effort'}`}:
              <InlineEffortBox
                label={`${attributes?.effort?.label ?? 'Effort'}`}
                actId={dataAiList.id}
                value={dataAiList.effort}
                values={attributes?.effort?.values ?? []}
                isEnabled={dataAiList.disableStatus}
                onClickEvent={this.updateEffortValue}
                onShowPopup={this.showEffortPopUp}
              />
            </div>
          </div>

          <div style={{ clear: 'both' }} />
          <div className="displayTable" style={{ paddingTop: '10px' }}>
            <div
              className="displayCell"
              style={{ fontSize: '11px', color: '#333', lineHeight: '16px' }}
              data-enable={dataAiList.disable}
              onClick={() => this.startDateClick()}
            >
              {t('Start Date:', { nsSeparator: false })}
              <div
                id="ai-start-date-optionedit"
                className="displayTable wbDate clickable wbDatePickStart "
                data-enable={dataAiList.disable}
                style={{
                  border: '1px solid #DCE0E3',
                  height: '28px',
                  borderRadius: '6px',
                }}
              >
                <div className="displayRow">
                  <div
                    className="displayCell"
                    style={{
                      width: '99px',
                      verticalAlign: 'middle',
                      textAlign: 'center',
                    }}
                  >
                    <div className="startDateAI">&nbsp;</div>
                    <div
                      className="wbDatePickStart"
                      id={`wbDatePickStart_${dataAiList.id}`}
                      style={{ float: 'left', padding: '1px 0 0 6px' }}
                    >
                      {startDateText}
                      <DayPickerInput
                        ref={this.startDatePicker}
                        format="MMM DD, YYYY"
                        value={startDateValue}
                        formatDate={formatDate}
                        parseDate={parseDate}
                        // placeholder={this.state.clearDate ? "No Date": "None"}
                        inputProps={{
                          id: `nactEditStartDate${dataAiList.id}`,
                          name: 'start_date',
                          type: 'text',
                          readOnly: true,
                          autoComplete: 'off',
                          style: { height: '0px', width: '0px', border: '0px' },
                        }}
                        dayPickerProps={{
                          ...startDateDisabledDays,
                          todayButton: 'Clear',
                          onTodayButtonClick: () =>
                            this.cleanDate(this.startDatePicker, 'start_date'),
                        }}
                        keepFocus={false}
                        onDayChange={this.saveStartDate}
                        // onClick={() => this.showDatePicker()}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div
              className="displayCell ai-due-date"
              style={{
                fontSize: '11px',
                color: '#333',
                lineHeight: '16px',
                paddingLeft: '18px',
              }}
              data-enable={dataAiList.disable}
              onClick={() => this.dueDateClick()}
            >
              {t('End Date:', { nsSeparator: false })}
              <div
                className="displayTable wbDate clickable wbDatePick "
                data-enable={dataAiList.disable}
                style={{
                  border: '1px solid #DCE0E3',
                  height: '28px',
                  borderRadius: ' 6px',
                }}
              >
                <div className="displayRow">
                  <div
                    className="displayCell"
                    style={{
                      width: '99px',
                      verticalAlign: 'middle',
                      textAlign: 'center',
                    }}
                  >
                    <div className="dueDateAI">&nbsp;</div>
                    <div
                      className="wbDatePick"
                      id={`wbDatePick_${dataAiList.id}`}
                      style={{ float: 'left', padding: '1px 0 0 6px' }}
                    >
                      {dueDateText}
                      <DayPickerInput
                        ref={this.dueDatePicker}
                        format="MMM DD, YYYY"
                        value={dueDateValue}
                        formatDate={formatDate}
                        parseDate={parseDate}
                        // placeholder={this.state.clearDate ? "No Date": "None"}
                        inputProps={{
                          id: `nactEditDueDate${dataAiList.id}`,
                          name: 'due_date',
                          type: 'text',
                          readOnly: true,
                          autoComplete: 'off',
                          style: { height: '0px', width: '0px', border: '0px' },
                        }}
                        dayPickerProps={{
                          ...endDateDisabledDays,
                          todayButton: 'Clear',
                          onTodayButtonClick: () =>
                            this.cleanDate(this.dueDatePicker, 'due_date'),
                        }}
                        keepFocus={false}
                        onDayChange={this.saveEndDate}
                        // onClick={() => this.showDatePicker()}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div style={{ clear: 'both' }} />

          <div
            className="displayCell"
            style={{
              paddingTop: '10px',
              fontSize: '11px',
              color: '#333',
              lineHeight: '16px',
            }}
          >
            {' '}
            {t('Reminder:', { nsSeparator: false })}
            <div onClick={this.remindMeAiClick}>
              <div className="dateTimeContainer1 reminder">
                <div
                  id={`reminderText_${dataAiList.id}`}
                  className="reminderText"
                  style={{
                    height: '0px',
                    width: '58px',
                    border: '0px',
                    lineHeight: reminderData ? '' : '28px',
                  }}
                >
                  {reminderData
                    ? moment(reminderData).format('MMM D hh:mm a')
                    : 'None'}
                </div>
              </div>
              <input
                id={`remindMiniEdit${dataAiList.id}`}
                className=""
                type="text"
                name="publish_time"
                style={{ height: '0px', width: '0px', border: '0px' }}
              />
            </div>
          </div>
          <div style={{ clear: 'both' }} />
          <div className="displayTable">
            <div
              className="displayCell wbRagAiEdit"
              style={{
                paddingTop: '10px',
                fontSize: '11px',
                color: '#333',
                lineHeight: '16px',
                width: '50%',
              }}
            >
              {`${attributes?.rag?.label ?? 'Confidence Rating'}`}:
              <InlineStateIconBox
                name="rag"
                values={attributes?.rag?.values ?? []}
                value={dataAiList.rag}
                isEnabled={dataAiList.disable}
                actId={dataAiList.id}
                onClickEvent={this.updateRagValue}
              />
            </div>
            <div
              className="displayCell"
              style={{
                paddingLeft: '35px',
                paddingTop: '10px',
                fontSize: '11px',
                color: '#333',
                lineHeight: '16px',
              }}
            >
              <span>{t('Delegate:', { nsSeparator: false })}</span>
              <div
                className="assigneeSelected arrowActive  minEditAssignee"
                data-enable={dataAiList.disable}
                data-attr={dataAiList.id}
                data-status={dataAiList.owner}
                onClick={(e) =>
                  this.onShowListPopup(e, 'owner', dataAiList.owner)
                }
                data-url={this.props.dataAiList.getAsgniList}
              >
                <span />
              </div>

              <div className="miniEditVrtArrow" />
              <div
                className="miniEditAssigniWrap wbActPopup clickable"
                data-attr={dataAiList.id}
                data-status={dataAiList.owner}
              />
              <div className="wsBoardOwnerListWrapper wsBoardAiAttrListDiv wsBoardAiAttr">
                {this.state.showListPopup == 'owner' ? (
                  <OwnerListComp
                    hideShowListPopup={this.hideShowListPopup}
                    aiId={dataAiList.id}
                    aiAssigniList={this.state.aiAssigniList}
                    canAddNonMember={dataAiList.canAddNonMember}
                    updateAssigniUrl={this.props.dataAiList.updateAssigniUrl}
                    actions={this.props.actions}
                    leftSpace={this.state.leftSpace}
                    topSpace={this.state.topSpace}
                    t={t}
                  />
                ) : null}
              </div>
            </div>
          </div>

          <div className="displayTable">
            <div
              className="displayCell wbPriorityAiEdit"
              style={{ fontSize: '11px', color: '#333', lineHeight: '16px' }}
            >
              {`${attributes?.priority?.label ?? 'Priority'}`}:
              <InlineStateIconBox
                name="priority"
                values={attributes?.priority?.values ?? []}
                value={dataAiList.priority}
                isEnabled={dataAiList.disable}
                actId={dataAiList.id}
                onClickEvent={this.updatePriorityValue}
              />
            </div>
            <div
              className="displayCell"
              style={{
                paddingLeft: '35px',
                paddingTop: '10px',
                fontSize: '11px',
                color: '#333',
                lineHeight: '16px',
              }}
            >
              {enablDeleteSec && <span> {t('Delete:', { nsSeparator: false })}</span>}
              {enablDeleteSec && (
                <div
                  className="delete"
                  id="deleteAIDiv"
                  onClick={(e) => this.deleteAI(e, dataAiList.id)}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }
}

const AiCard = (props) => {
  let clickCount = 0
  let singleClickTimer = null
  const [isOpenAI, setIsOpenAI] = useState(false)
  const [boxStyle, setBoxStyle] = useState({})
  const [deleteAiPopupData, setDeleteAiPopupData] = useState(null)
  const [aiRef] = useOnClickOutside(() => {
    if ($('#ui-datepicker-div').css('display') === 'none') {
      setIsOpenAI(false);
    }
  })
  const { t } = useTranslation();

  const editActionItem = (e, id, url) => {
    e.persist()
    e.preventDefault()
    clickCount++
    if (clickCount === 1) {
      singleClickTimer = setTimeout(() => {
        if (clickCount === 1) {
          clickCount = 0
          setBoxStyle(e.target.getBoundingClientRect())
          setIsOpenAI(true)
        }
      }, 300)
    } else if (clickCount === 2) {
      clearTimeout(singleClickTimer)
      clickCount = 0
      if (id.indexOf('id=newAi_') === -1) {
        openFullPopUp(
          aiFullViewPopUp,
          `${wbConfig.baseUrl}/activity/aI?id=${id}`,
          e.target
        )
      }
    }
  }

  const handleDelete = () => {
    const { actId, meetingId } = deleteAiPopupData
    if (actId) {
      props.actions.MyworkDeleteActionItem(actId)
      const url = props.myWorkData.deleteActUrl
      $.post(url, { act_id: actId, ajax: true, meetingId }, () => {}).done(
        () => {
          myworkCount()
        }
      )
    }
    setDeleteAiPopupData(null)
  }

  const shouldAiShow = (dataAiList) => {
    /**
     * ai state checking
     * state constants mapping
     * const STATE_DONE = 0;
     * const STATE_DOING = 1;
     * const STATE_NEXT = 2;
     * const STATE_REDFLAG = 3;
     * const STATE_PAUSE = 4;
     */
    let aiStatus = 'doing'
    switch (dataAiList.state) {
      case '2':
        aiStatus = 'next'
        break
      case '4':
        aiStatus = 'pause'
        break
    }
    /**
     * rating state checking
     * rating constant mapping
     * const RAG_GREEN = 0;
     * const RAG_AMBER = 1;
     * const RAG_RED = 2;
     */
    let aiRating = 'green'
    switch (dataAiList.rag) {
      case '1':
        aiRating = 'amber'
        break
      case '2':
        aiRating = 'red'
        break
    }
    /**
     * priority state checking
     * priority constants mapping
     * const PRIORITY_LOW = 3;
     * const PRIORITY_MED = 2;
     * const PRIORITY_HIGH = 1;
     */
    let aiPriority = 'medium'
    switch (dataAiList.priority) {
      case '1':
        aiPriority = 'high'
        break
      case '3':
        aiPriority = 'low'
        break
    }
    /**
     * sub-actions checking
     */
    let showSubaction = true
    if (!props.showSubactions) {
      showSubaction = dataAiList.parent === null
    }
    /**
     * workstreams id checking
     */
    const checkByWorkstreams =
      typeof props.aiWorkstreamIds !== 'undefined'
        ? props.aiWorkstreamIds.size > 0
        : false
    let showByWorkstream = true
    if (checkByWorkstreams) {
      showByWorkstream = props.aiWorkstreamIds.has(dataAiList.boardId)
    }
    /**
     * tags id checking
     */
    const checkByTags =
      typeof props.aiTagIds !== 'undefined' ? props.aiTagIds.size > 0 : false
    let showByTag = true
    if (checkByTags) {
      showByTag = false
      /** if user select only 1 tag, it's OR condition compare to ai tags list */
      if (props.aiTagIds.size === 1) {
        for (let i = 0; i < dataAiList.aiTags.length; i++) {
          const tag = dataAiList.aiTags[i]
          if (props.aiTagIds.has(tag.id)) {
            showByTag = true
          }
        }
      } else {
        /** if user select >= 2 tags, it's AND condition compare to ai tags list */
        let matchCount = 0
        for (let i = 0; i < dataAiList.aiTags.length; i++) {
          const tag = dataAiList.aiTags[i]
          if (props.aiTagIds.has(tag.id)) {
            matchCount++
          }
        }
        showByTag = matchCount === props.aiTagIds.size
      }
    }

    return (
      props.aiStatus.has(aiStatus) &&
      props.aiRating.has(aiRating) &&
      props.aiPriority.has(aiPriority) &&
      showSubaction &&
      showByWorkstream &&
      showByTag
    )
  }
  const {
    dataAiList,
    calView,
    TagChek,
    teamView,
    teamId,
    editAIURL,
    currentUserId,
    isTeamManager,
    list,
  } = props
  const canEditActionItem = dataAiList.canEditAI ? '' : 'unsortable'
  // check if this action item is private
  const { privateAi } = dataAiList
  let wsName = dataAiList.board
  if (privateAi) {
    wsName = 'Private Request'
  }
  let rating = { borderLeft: '4px solid #78c047' }
  if (dataAiList.rag == 1) {
    rating = { borderLeft: '4px solid rgb(234, 162, 65)' }
  } else if (dataAiList.rag == 2) {
    rating = { borderLeft: '4px solid #da3d26' }
  }
  const teamViewField = teamView != ''
  const ChekTag = TagChek == ''
  const parentId = dataAiList.parent !== null
  const statusEnable =
    dataAiList.owner == currentUserId || isTeamManager ? ' statusEnable ' : ''
  const currentUser = dataAiList.owner == currentUserId ? ' userAi ' : ''
  const parentOrgRole =
    dataAiList.parentOrgRole != 'undefined' ? dataAiList.parentOrgRole : ''
  const dueDate = dataAiList.due_date
    ? moment.utc(dataAiList.due_date * 1000).format('MM/DD')
    : ''
  const isShowAi = shouldAiShow(dataAiList)
  const { attributes, icons } = getAiTypeAttributes(dataAiList.type)
  const activityTypeName = getActivityTypeName(dataAiList.type).toLowerCase();
  return (
    <div>
      {deleteAiPopupData && (
        <ThemeProvider>
          <Confirm
            isOpen
            kind="normal"
            title="Confirm Deletion"
            submitLabel="Delete"
            disableScrollLock
            onDismiss={() => setDeleteAiPopupData(null)}
            onSubmit={handleDelete}
          >
            {t('Are you sure you want to delete this')} {activityTypeName}?
          </Confirm>
        </ThemeProvider>
      )}
      <div
        className={`aiContainer ${canEditActionItem}${statusEnable}${currentUser} ${
          !isShowAi ? ' hide ' : ''
        }`}
        data-effort={dataAiList.effort}
        data-activity-id={dataAiList.id}
        data-value={dataAiList.due_date}
        data-col={props.col}
      >
        <div
          className={`aiBlock ${canEditActionItem} bossDelegateAi_${parentOrgRole}`}
          data-actid={dataAiList.id}
          id={`aiBlock_${dataAiList.id}`}
          style={rating}
          data-url={`${editAIURL}?id=${dataAiList.id}`}
          onClick={(e) => editActionItem(e, dataAiList.id, editAIURL)}
          data-value={dataAiList.due_date}
        >
          <div className="aiDesc truncateText" style={{ whiteSpace: 'normal' }}>
            {privateAi != '' && TagChek != '' && (
              <div
                className="aiAttr pull-left privateAiAttr"
                style={{ width: '17px' }}
              >
                &nbsp;
              </div>
            )}
            {parentId && TagChek != '' && (
              <div
                className="subActionLinkThisWeek pull-left aiAttr"
                style={{ marginBottom: '0px', marginTop: '3px' }}
              >
                &nbsp;
              </div>
            )}
            <span
              className="editThisWeekParentDesc"
              dangerouslySetInnerHTML={{ __html: dataAiList.description }}
            />
          </div>
          {TagChek && teamViewField && (
            <div
              className="wsTitle  truncateText"
              title={dataAiList.owner_firstname + dataAiList.owner_lastname}
            >
              {dataAiList.owner_firstname} {dataAiList.owner_lastname}
            </div>
          )}
          {ChekTag && (
            <div className="wsTitle truncateText" title={wsName}>
              {' '}
              {wsName}
            </div>
          )}
          <AiAttrComp
            dataAiList={dataAiList}
            privateAi={privateAi}
            TagChek={TagChek}
            calView={calView}
          />
          <AiChipContainer>
            <div className="ai-chip-left-container">
              {TagChek != '' && (
                <div>
                  <Icon
                    name={getStateComponentIcon(
                      attributes?.state?.values,
                      dataAiList.state
                    )}
                    width="15"
                    height="15"
                  />
                </div>
              )}
              {TagChek != '' && (
                <>
                  <div>
                    <Icon
                      name={getStateComponentIcon(
                        attributes?.effort?.values,
                        dataAiList.effort
                      )}
                      width="15"
                      height="15"
                    />
                  </div>
                  <div className="ownerFirstName truncateText">
                    {dataAiList.owner_firstname}
                  </div>
                </>
              )}
              {teamId != '' &&
                calView != 'month' &&
                teamId != '' &&
                calView == 'week' &&
                TagChek == '' && (
                  <div
                    className="ownerFirstName truncateText"
                    style={{ width: '150px', paddingLeft: '0px' }}
                  >
                    {dataAiList.owner_firstname}
                  </div>
                )}
            </div>
            {TagChek != '' && (
              <div className="ai-chip-right-container">
                <AiDueDateText isLateDate={dataAiList.lateDate == 1}>
                  {dueDate}
                </AiDueDateText>
              </div>
            )}
          </AiChipContainer>
        </div>
        {isOpenAI && (
          <EditAiSmallPopUp
            dataAiList={dataAiList}
            actions={props.actions}
            myWorkData={props.myWorkData}
            menuKeepOpen={props.menuKeepOpen}
            handleDeleteAi={setDeleteAiPopupData}
            calView={calView}
            list={list}
            TagChek={TagChek}
            ref={aiRef}
            boxStyle={boxStyle}
            t={t}
          />
        )}
      </div>
    </div>
  )
}

class OwnerListComp extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      aiAssigniLists: [],
      query: '',
    }
    this.getInfo = this.getInfo.bind(this)
    this.searchAssigni = this.searchAssigni.bind(this)
    this.onChangeOwner = this.onChangeOwner.bind(this)
    this.nonMemberAddEmail = this.nonMemberAddEmail.bind(this)
    this.search = React.createRef()
    this.nonMember = React.createRef()
    this.inputFocus = this.inputFocus.bind(this)
    this.nonMemberFocus = this.nonMemberFocus.bind(this)
  }

  componentDidMount() {
    this.inputFocus()
  }

  inputFocus() {
    if (this.search.current) {
      this.search.current.focus()
    }
  }

  nonMemberFocus() {
    if (this.nonMember.current) {
      this.nonMember.current.focus()
    }
  }

  getInfo() {
    const _this = this
    if (this.state.query.length >= 3) {
      $.ajax({
        url: `${wbConfig.baseUrl}/activity/GetOrgUsers`,
        type: 'get',
        data: { term: this.state.query },
      }).done((response) => {
        if (response.length > 0) {
          const assigniL = JSON.parse(response)
          _this.setState({
            aiAssigniLists: assigniL,
          })
        }
      })
    }
    // }
  }

  searchAssigni(e) {
    this.setState(
      {
        query: e.target.value,
      },
      () => {
        if (this.state.query && this.state.query.length > 1) {
          // if (this.state.query.length % 2 === 0) {
          this.getInfo()
          // }
        }
      }
    )
  }

  onChangeOwner(item) {
    const { actions } = this.props
    const { aiId } = this.props
    if (aiId) {
      this.props.hideShowListPopup()
      let private_req = ''
      if (item.wsAccess == 'yes') {
        private_req = 'no'
      } else {
        private_req = 'yes'
      }
      actions.MyworkDelegateActionItem(aiId)
      $.post(
        `${wbConfig.baseUrl}/activity/UpdateAssigni`,
        {
          assign: item.id,
          act_id: aiId,
          wsBoardView: true,
          private_req,
        },
        (data, status) => {}
      )
    }
  }

  nonMemberAddEmail(e) {
    $('.invalidEmailMsg').hide()
    // $('.newUserEmailDiv').css("height", "72px");
    if (e.keyCode == 13) {
      const { actions } = this.props
      const actID = this.props.aiId
      const userEmailEntered = $(e.target).val()
      const re =
        /^[_A-Za-z0-9-+]+(\.[_A-Za-z0-9-+]+)*@[A-Za-z0-9-]+(\.[a-z0-9-]+)*(\.[A-Za-z]{2,4})$/
      const emailValidate = re.test(userEmailEntered)
      if (emailValidate) {
        const obj = e.target
        const teamID = $(this).closest('.WbActRow').attr('data-teamid')
        $(e.target).val('')
        const url = this.props.updateAssigniUrl
        actions.MyworkDelegateActionItem(actID)
        $('.editAIWrap').hide()
        $('.miniEditVrtArrow').hide()
        $.post(
          url,
          {
            assign: userEmailEntered,
            act_id: actID,
            team_id: teamID,
            process: 'nonMemberAdd',
          },
          (data, status) => {
            if (data.status == 'success') {
              myworkCount()
            } else if (data.status == 'error') {
              $(obj).closest('.wbActPopup').hide()
              $(obj)
                .closest('.wbActPopup')
                .siblings('.wbOwner')
                .removeClass('wbWhiteBGArrow')
              alert(data.errorMsg)
            }
          }
        )
      } else {
        $('.invalidEmailMsg').show()
        $('.newUserEmailDiv').css('height', '87px')
        return false
      }
    }
  }

  render() {
    const { t } = this.props
    const canAddNonMember = this.props.canAddNonMember === 'true'
    return (
      <div
        className="wsBoardOwnerListWrapperDiv"
        style={{ left: this.props.leftSpace, top: this.props.topSpace }}
      >
        <input
          type="text"
          className="wsBoardAssigniSearch gbl-text-entry"
          id="wsBoardAssigniSearch"
          onChange={(e) => this.searchAssigni(e)}
          ref={this.search}
          onClick={() => this.inputFocus()}
        />
        {this.state.aiAssigniLists.length ? (
          <div className="wsBoardOwnerListDiv">
            {this.state.aiAssigniLists.length
              ? this.state.aiAssigniLists.map((item, index) => (
                  <div
                    key={`assign_row_${index}`}
                    className="wsBoardUser clickable"
                    onClick={() => this.onChangeOwner(item)}
                  >
                    {item.value}
                  </div>
                ))
              : ''}
          </div>
        ) : (
          ''
        )}
        {canAddNonMember && (
          <div
            className="newUserEmailDiv"
            style={{
              backgroundColor: '#f1f7f8',
              height: '72px',
              textAlign: 'center',
            }}
          >
            <div
              style={{
                padding: '5px 0',
                fontSize: '12px',
                color: '#000000',
                fontStyle: 'italic',
              }}
            >
              {t("Assign to someone else (they'll be added to the team)")}
            </div>
            <div>
              <input
                type="text"
                className="assignNonMember"
                id="nonMemberAddInput"
                name="nonMemberAdd"
                placeholder="Enter email"
                onKeyUp={this.nonMemberAddEmail}
                ref={this.nonMember}
                onClick={() => this.nonMemberFocus()}
              />
            </div>
            <div
              className="invalidEmailMsg disabledWarningText"
              style={{
                padding: '5px 5px 5px 15px',
                display: 'none',
                textAlign: 'left',
              }}
            >
              {t("Please enter valid email")}.
            </div>
          </div>
        )}
      </div>
    )
  }
}

const enhance = compose(withTranslation(), hot)
export default enhance(MyworkApp)
export { TopSectionContainer }
